import React, { useState, useEffect, useRef, useRef as useRefAlias, useCallback, useMemo } from "react";
import { Send, Check, TriangleAlert, Upload, Grid2x2Plus, Plus, Image, ImageOff, Menu, ListRestart, User, Bot, Loader, Edit, Save, ChevronUp, ChevronDown, FilePlus, Mic, WandSparkles, Info, Mail, LogOut, X, ChevronLeft, ChevronRight, Grid2x2, Sliders, HelpCircle } from "lucide-react";
import InfoPopup from "../components/info";
import ImageEditor from "../components/imageMask";
import FileUpload, { convertImageToBase64, extractContentFromFiles, queryRAGFiles, isImageFile } from '../components/fileUpload';
import AudioTranscription from '../components/transcription';
import PDFUpload from '../components/pptxHandler';

import AssistantMarkdown from "../components/AssistantMarkdown";

import NewsletterPopup from "../components/NewsletterPopup";
import UpdatesPopup from "../components/UpdatesPopup";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { remove } from "jszip";




const formatResponseWithCitations = (content, citations) => {
  if (!citations || citations.length === 0) {
    return content;
  }

  // Replace citation patterns in the response with clickable links
  let formattedContent = content.replace(/\[(\d+)\]/g, (match, number) => {
    const citationIndex = parseInt(number) - 1;
    if (citationIndex >= 0 && citationIndex < citations.length) {
      return `<a href="${citations[citationIndex]}" target="_blank" rel="noopener noreferrer" class="citation-link">[${number}]</a>`;
    }
    return match;
  });

  // Preserve paragraph breaks by replacing line breaks with proper spacing
  formattedContent = formattedContent.replace(/\n\n/g, '<br><br>');

  return formattedContent;
};


function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return isMobile;
}

// function useIsMobile() {
//   const [isMobile, setIsMobile] = React.useState(false);

//   React.useEffect(() => {
//     // Mobile-like devices usually: no hover + coarse pointer (touch)
//     const mq = (navigator.userAgentData?.mobile ?? false) || window.matchMedia("(hover: none) and (pointer: coarse)");

//     const update = () => setIsMobile(mq.matches);
//     update();

//     mq.addEventListener?.("change", update);
//     return () => mq.removeEventListener?.("change", update);
//   }, []);

//   return isMobile;
// }



// Convert your existing chatHistory to text-only turns for Gemini
function toTextOnlyMessages(history, maxTurns = 20) {
  const slice = history.slice(Math.max(history.length - maxTurns, 0));
  return slice
    .map((m) => {
      // normalize content → text
      let text = "";
      if (Array.isArray(m.content)) {
        text = m.content
          .filter(c => c?.type === "text" && typeof c.text === "string")
          .map(c => c.text.trim())
          .filter(Boolean)
          .join("\n\n");
      } else if (typeof m.content === "string") {
        text = m.content.trim();
      }
      if (!text) return null;
      // keep roles user/assistant; Lambda will map assistant → model
      return { role: m.role === "assistant" ? "assistant" : "user", content: [{ type: "text", text }] };
    })
    .filter(Boolean);
}

// Function Image Generation Mapping
function buildRefineRequest(selectedModel, systemPrompt, userPrompt, visualState, reasoningEffort, gptOssReasoning) {
  const refineInstruction = `
Current scene description: ${visualState || "None yet"}.
The user now says: "${userPrompt}".

Rewrite this into a vivid, detailed prompt for image generation.

Guidelines:
- Preserve all elements from the current scene (background, lighting, environment, mood, composition) unless the user explicitly asks for a change.
- Apply the user’s new instruction by modifying only the mentioned elements.
- Do not simplify or omit previously described details (e.g., sunsets, foliage, reflections).
- Expand into a richly detailed description including subject, environment, style, colors, and mood as appropriate.
- If user input is vague or generic, enhance with creative details to make the prompt specific and evocative.
- Keep descriptions to 2 - 3 sentences
`.trim();

  // console.log(`Current Refine Instruction: ${refineInstruction}`);


  let lambdaUrl;
  let payload;

  const O_SERIES = new Set(["o1", "o1-preview", "o1-mini", "o2", "o3", "o3-mini"]);
  if (O_SERIES.has(selectedModel)) {
    // Map to your existing lambda URLs for o-series
    const oSeriesConfig = {
      o1: {
        lambdaUrl: "https://keav6fptwkwfj5zrqolbqydelq0htpng.lambda-url.ap-southeast-1.on.aws/",
      },
      "o1-preview": {
        lambdaUrl: "https://keav6fptwkwfj5zrqolbqydelq0htpng.lambda-url.ap-southeast-1.on.aws/",
      },
      "o1-mini": {
        // If you have a different endpoint for o1-mini, update here. Using the same as o1 by default.
        lambdaUrl: "https://keav6fptwkwfj5zrqolbqydelq0htpng.lambda-url.ap-southeast-1.on.aws/",
      },
      o2: {
        // Optional; route like o1 unless you have a dedicated lambda
        lambdaUrl: "https://keav6fptwkwfj5zrqolbqydelq0htpng.lambda-url.ap-southeast-1.on.aws/",
      },
      o3: {
        lambdaUrl: "https://keav6fptwkwfj5zrqolbqydelq0htpng.lambda-url.ap-southeast-1.on.aws/",
      },
      "o3-mini": {
        lambdaUrl: "https://qipgrgsrzr57wb3lvmpjjwuqy40wyxwl.lambda-url.ap-southeast-1.on.aws/",
      },
    };

    const cfg = oSeriesConfig[selectedModel];
    lambdaUrl = cfg.lambdaUrl;
    payload = {
      model: selectedModel,
      systemPrompt,
      // For refinement we pass the refine instruction as the "enhancedUserPrompt"
      enhancedUserPrompt: refineInstruction,
      reasoningEffort: reasoningEffort || "medium",
    };

    return { lambdaUrl, payload };
  }

  // Azure series (Image Gen)
  else if (selectedModel.startsWith("azure-")) {
    const modelName = selectedModel.replace("azure-", "");

    const azureModelConfig = {
      "o1-mini": {
        lambdaUrl: "https://7zjhepnjryffab7s3yew5mnxoi0umjfp.lambda-url.ap-southeast-1.on.aws/",
        payload: {
          azure_model_endpoint: modelName,
          messages: [{ role: "user", content: refineInstruction }],
        },
      },
      o1: {
        lambdaUrl: "https://7zjhepnjryffab7s3yew5mnxoi0umjfp.lambda-url.ap-southeast-1.on.aws/",
        payload: {
          azure_model_endpoint: modelName,
          messages: [
            { role: "system", content: systemPrompt },
            { role: "user", content: refineInstruction },
          ],
          reasoning_effort: reasoningEffort || "medium",
        },
      },
      "o3-mini": {
        lambdaUrl: "https://j3fzcjerdlodtmp45jaqdeh6hi0cbhdo.lambda-url.ap-southeast-1.on.aws/",
        payload: {
          azure_model_endpoint: modelName,
          messages: [
            { role: "system", content: systemPrompt },
            { role: "user", content: refineInstruction },
          ],
          reasoning_effort: reasoningEffort || "medium",
        },
      },
      default: {
        lambdaUrl: "https://j3fzcjerdlodtmp45jaqdeh6hi0cbhdo.lambda-url.ap-southeast-1.on.aws/",
        payload: {
          azure_model_endpoint: modelName,
          messages: [
            { role: "system", content: systemPrompt },
            { role: "user", content: refineInstruction },
          ],
        },
      },
    };

    const config = azureModelConfig[modelName] || azureModelConfig.default;
    lambdaUrl = config.lambdaUrl;
    payload = config.payload;
  }
  

  else if (
    selectedModel.startsWith("o3-deep-research") ||
    selectedModel.startsWith("o4-mini-deep-research")
  ) {
    lambdaUrl =
      "https://ofvmz63qifkrjqyc2fhmcuugfe0livoe.lambda-url.ap-southeast-1.on.aws/";

    const deepResearchModelMap = {
      "o3-deep-research": "o3-deep-research-2025-06-26",
      "o4-mini-deep-research": "o4-mini-deep-research-2025-06-26",
    };

    const actualModelId = deepResearchModelMap[selectedModel] || selectedModel;

    payload = {
      model: actualModelId,
      input: [
        {
          role: "developer",
          content: [
            { type: "input_text", text: systemPrompt }
          ]
        },
        {
          role: "user",
          content: [
            { type: "input_text", text: refineInstruction }
          ]
        }
      ],
      reasoning: { summary: "auto" },
      tools: [{ type: "web_search_preview" }]
    };

    return { lambdaUrl, payload };
  }

  // Claude series (Image Gen)
  else if (selectedModel.startsWith("claude-") || selectedModel.startsWith("nova-")) {
    const modelName = selectedModel.startsWith("claude-")
      ? selectedModel.replace("claude-", "")
      : selectedModel;

    const models = {
      "3.7-sonnet": "us.anthropic.claude-3-7-sonnet-20250219-v1:0",
      "3.5-haiku": "us.anthropic.claude-3-5-haiku-20241022-v1:0",
      "3.5-sonnet": "us.anthropic.claude-3-5-sonnet-20240620-v1:0",
      "3.5-sonnet-v2": "us.anthropic.claude-3-5-sonnet-20241022-v2:0",
      "4-sonnet": "us.anthropic.claude-sonnet-4-20250514-v1:0",
      "nova-lite-v1": "us.amazon.nova-lite-v1:0",
    };

    lambdaUrl = "https://3u54y2lyqjhzck6rq6memyokay0jbrmo.lambda-url.ap-southeast-1.on.aws/";

    if (models[modelName]) {
      payload = {
        model: models[modelName],
        max_tokens: 1000,
        system: systemPrompt,
        user_input: refineInstruction,
      };
    }
  }

  // Gemini series (Image Gen)
  else if (selectedModel.startsWith("gemini-")) {
    lambdaUrl = "https://wik67h2ozhuefrn43jh3giuuli0kzlvo.lambda-url.ap-southeast-1.on.aws/";
    payload = {
      model: selectedModel,
      messages: [
        { role: "system", content: [{ type: "text", text: systemPrompt }] },
        { role: "user", content: [{ type: "text", text: refineInstruction }] }
      ]
    };
  }

  // Sealion series (Image Gen)
  else if (selectedModel.startsWith("sealion-")) {
    lambdaUrl = "https://uge4yvc5v6qq3wpwy3d4rwn6hy0cwors.lambda-url.ap-southeast-1.on.aws/";
    const modelMapping = {
      "sealion-v3.5-70B-R": "aisingapore/Llama-SEA-LION-v3.5-70B-R",
      "sealion-v3-70B-IT": "aisingapore/Llama-SEA-LION-v3-70B-IT",
      "sealion-v3-8B-IT": "aisingapore/Gemma-SEA-LION-v3-9B-IT",
      "sealion-v3.5-8B-R": "aisingapore/Llama-SEA-LION-v3.5-8B-R"
    }

    const actualModelId = modelMapping[selectedModel] || selectedModel;

    payload = {
      model: actualModelId,
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: refineInstruction }
      ],
      max_tokens: 1000,
      temperature: 0.7
    };
  }

  // Deepseek series (Image Gen)
  else if (selectedModel.startsWith("deepseek-")) {
    lambdaUrl = "https://xji6ujhm6qmcfaxk7c3kfpcol40sflvm.lambda-url.ap-southeast-1.on.aws/";
    payload = {
      prompt: refineInstruction,
      model: selectedModel
    };
  }

  /// Perplexity series (Image Gen)
  else if (selectedModel.startsWith("sonar")) {
    lambdaUrl = "https://zkm4ckouspeofiqlr6a5bgrz3m0lmwyk.lambda-url.ap-southeast-1.on.aws/";
    payload = {
      model: selectedModel,
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: refineInstruction }
      ],
      max_tokens: 4000,
      temperature: 0.2
    };
  }

  // GPT-OSS series (Image Gen)
  else if (selectedModel.startsWith("gpt-oss-")) {
    lambdaUrl = "https://7ch2vcujikhex6ixqfwgspbjvy0naudr.lambda-url.ap-southeast-1.on.aws/";

    // console.log(`GPT-OSS reasoning: ${gptOssReasoning}`);
    const gptOssSystemPrompt = `${systemPrompt}\n\nReasoning: ${gptOssReasoning}`;
    payload = {
      prompt: refineInstruction,
      system_prompt: gptOssSystemPrompt,
      model: selectedModel
    };
  }

  else if (selectedModel.includes("codex") || selectedModel === "gpt-5.1") {
    lambdaUrl = "https://2xzetkss4nybfugbl4oufeuaje0txcdv.lambda-url.ap-southeast-1.on.aws/";
    payload = {
      model: selectedModel,
      systemPrompt,
      enhancedUserPrompt: refineInstruction
    };
  }


  // ZHM CHANGES
  else {
    lambdaUrl = "https://qipgrgsrzr57wb3lvmpjjwuqy40wyxwl.lambda-url.ap-southeast-1.on.aws/";
    payload = {
      model: selectedModel,
      systemPrompt,
      enhancedUserPrompt: refineInstruction
    };
  }
  // END ZHM CHANGES

  return { lambdaUrl, payload };
}

// Strip <think> and <reasoning> tags 
function stripThinkTags(text = "") {
  if (!text) return "";

  return text
    .replace(/<think>[\s\S]*?<\/think>/gi, "")
    .replace(/<reasoning>[\s\S]*?<\/reasoning>/gi, "")
    .trim();
}

const GeneratingImageLoader = ({ label = "Generating your image...", isMobile = false }) => (
  <span className={`inline-flex items-center ${isMobile ? "text-sm" : "text-lg"} text-neutral-300`}>
    <span className="relative overflow-hidden">
      <span className="thinking-shimmer">{label}</span>
    </span>
    <span className="ml-1 text-neutral-400">›</span>
  </span>
);


function TypingDots() {
  return (
    <div className="flex justify-center items-center h-6">
      <div className="flex gap-1 m-auto">
        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
      </div>
    </div>
  );
}


async function parseRefineResponse(selectedModel, response) {
  const raw = await response.text();
  // console.log(`Raw refine resposne for ${selectedModel}: ${raw}`);
  let data;
  try { data = JSON.parse(raw); } catch { return stripThinkTags(raw.trim()); }

  if (data && typeof data === "object" && typeof data.body === "string" && !data.output) {
    try { data = JSON.parse(data.body); } catch { }
  }

  const result =
    data?.response?.trim?.() ||
    data?.output?.[0]?.content?.[0]?.text?.trim?.() ||
    data?.choices?.[0]?.message?.content?.trim?.() ||
    data?.text?.trim?.() ||
    (typeof data === "string" ? data.trim() : "");

  return stripThinkTags(result);
}

function GenericAI({ handleLogoutRedirect }) {
  const [userPrompt, setUserPrompt] = useState("");
  // const [systemPrompt, setSystemPrompt] = useState("You are a helpful assistant. Use fenced code blocks (```) with language identifiers for code.");
  const DEFAULT_SYSTEM_PROMPT =
    'You are a helpful assistant. Use fenced code blocks (```) with language identifiers for code.';

  const [systemPrompt, setSystemPrompt] = React.useState(DEFAULT_SYSTEM_PROMPT);

  // NEW: tracks whether the user has ever saved a prompt
  const [hasSavedSystemPrompt, setHasSavedSystemPrompt] = React.useState(false);

  const [selectedModel, setSelectedModel] = useState("gpt-4o");
  const [chatHistory, setChatHistory] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [textAreaRows, setTextAreaRows] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isPromptVisible, setIsPromptVisible] = useState(false); // State for collapse/expand system prompt
  const [warningMessage, setWarningMessage] = useState("");
  // WJ CHANGE
  const [infoMessage, setInfoMessage] = useState("");
  // END OF WJ CHANGE
  const [fileWarningMessage, setFileWarningMessage] = useState("");
  const [isFilePopupOpen, setIsFilePopupOpen] = useState(false);
  const [extractedText, setExtractedText] = useState(""); // Text from Word files
  const [fileList, setFileList] = useState([]); // List of uploaded files
  const [reasoningEffort, setReasoningEffort] = useState("medium"); //reasoning level of o3-mini
  // const [selectedImageModel, setSelectedImageModel] = useState("none");
  const [imageToEdit, setImageToEdit] = useState(null);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [maskPreview, setMaskPreview] = useState(null);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [maskSavedMessage, setMaskSavedMessage] = useState(false);
  const [isInfoOpen, setisInfoOpen] = useState(false);
  const [imageResponseId, setImageResponseId] = useState(null);
  const [isS3UploadEnabled, setIsS3UploadEnabled] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadMode, setUploadMode] = useState('context');
  const isDirectUploadModelSelected = selectedModel === "gpt-4o" || selectedModel === "gpt-4o-mini" || selectedModel === "gpt-5" || selectedModel === "gpt-5-mini" || selectedModel === "gpt-5-nano";
  // const [isModelLoading, setIsModelLoading] = useState(false);
  // const [modelStatus, setModelStatus] = useState("unknown")
  const [isAudioPopupOpen, setIsAudioPopupOpen] = useState(false);
  const [enableImageGen, setEnableImageGen] = useState(false);
  const [imageModel, setImageModel] = useState("gpt-4o-mini");
  const [isPDFUploadOpen, setIsPDFUploadOpen] = useState(false);

  const [gptOssReasoning, setGptOssReasoning] = useState("medium");

  const [visualState, setVisualState] = useState("");

  const taRef = useRef(null);

  // Image refining prompt UI
  const [isRefiningPrompt, setIsRefiningPrompt] = useState(false);

  const [isNewsletterOpen, setIsNewsletterOpen] = useState(false);
  const [isUpdatesOpen, setIsUpdatesOpen] = useState(true);

  const [selectedFilters, setSelectedFilters] = useState(["All"]);

  const [modelSearch, setModelSearch] = useState("");

  const [showSettings, setShowSettings] = useState(false);

  const IMAGE_CAPABLE_MODELS = new Set([
    "gpt-4o",
    "gpt-4o-mini",
    "gpt-5",
    "gpt-5-mini",
    "gpt-5-nano",
  ]);

  const scrollRef = useRef(null);
  const [autoScroll, setAutoScroll] = useState(true); // follow streaming messages

  // 1) Track whether user is near the bottom
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nearBottom = distanceFromBottom < 80; // px threshold

    setAutoScroll(nearBottom);
  };

  // 2) While streaming, keep scrolling down on every update
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (!autoScroll) return;
    if (!loading) return; // <- you should set this when assistant is streaming

    // no smooth here, it’ll be called many times per second
    el.scrollTop = el.scrollHeight;
  }, [chatHistory, loading, autoScroll]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    // After DOM/layout updates
    requestAnimationFrame(() => {
      // ChatGPT-style: always stick to the bottom so the newest message is visible
      // container.scrollTop = container.scrollHeight;
      // or with animation:
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    });
  }, [chatHistory.length]);



  useEffect(() => {
    if (!isPromptVisible) return;
    if (!editorRef.current) return;

    // First time (never saved): keep it empty so placeholder shows
    if (!hasSavedSystemPrompt) {
      editorRef.current.textContent = "";
      return;
    }

    // After user saved at least once: show current systemPrompt
    editorRef.current.textContent = systemPrompt || "";
  }, [isPromptVisible, hasSavedSystemPrompt, systemPrompt]);





  const [modelBarOpen, setModelBarOpen] = useState(false);
  const [isAdjustmentOpen, setisAdjustmentOpen] = useState(false);


  
  const [modelBarMounted, setModelBarMounted] = useState(false);
  const [dragY, setDragY] = useState(0);
  const dragStartYRef = useRef(null);

  useEffect(() => {
    if (modelBarOpen) {
      document.body.style.overflow = "hidden";  // Disable background scroll
    } else {
      document.body.style.overflow = "";  // Restore background scroll
    }

    // Cleanup when component unmounts or model bar is closed
    return () => {
      document.body.style.overflow = "";
    };
  }, [modelBarOpen]); // Trigger on modelBarOpen state change

  useEffect(() => {
    if (modelBarOpen) setModelBarMounted(true);
  }, [modelBarOpen]);

  const closeModelBar = () => {
    setDragY(0);
    setModelBarOpen(false);
  };

  const onGrabPointerDown = (e) => {
    // only drag on mobile sheet (grabber area)
    dragStartYRef.current = e.clientY;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onGrabPointerMove = (e) => {
    if (dragStartYRef.current == null) return;
    const dy = Math.max(0, e.clientY - dragStartYRef.current);
    const max = Math.round(window.innerHeight * 0.75);
    setDragY(Math.min(dy, max));
  };

  const onGrabPointerEnd = () => {
    if (dragStartYRef.current == null) return;
    dragStartYRef.current = null;

    const closeAt = Math.round(window.innerHeight * 0.15); // tweak if you want
    if (dragY > closeAt) closeModelBar();
    else setDragY(0);
  };



  
  const [fileProgress, setFileProgress] = useState({});
  const [processingFiles, setProcessingFiles] = useState([]);
  const [isProcessingUpload, setS3Processing] = useState(false);


  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext)) return '🖼️';
    if (ext === 'docx') return '📄';
    if (ext === 'pdf') return '📋';
    if (ext === 'pptx') return '📊';
    if (ext === 'zip') return '📦';
    return '📁';
  };
  

  // For the image generation model
  // const handleImageModelChange = (e) => {
  //   setSelectedImageModel(e.target.value);
  // };

  // Commented out 
  // const IMAGE_CAPABLE = new Set(["gpt-4o","gpt-4o-mini","gpt-5","gpt-5-mini","gpt-5-nano"]);

  useEffect(() => {
    if (IMAGE_CAPABLE_MODELS.has(selectedModel)) {
      setImageModel(selectedModel);   // image model = selected model
    } else {
      setImageModel("gpt-5");   // or keep previous; this is a safe fallback
    }
  }, [selectedModel]);


  const togglePromptVisibility = () => setIsPromptVisible(!isPromptVisible); // Toggle visibility

  // Function to toggle file popup
  const toggleFilePopup = () => {
    setIsFilePopupOpen(!isFilePopupOpen);
  };

  const modelCategories = {
    "Reasoning (Azure)": ["azure-o1", "azure-o1-mini", "azure-o3-mini"],
    "Azure OpenAI": ["azure-gpt-4o", "azure-gpt-4o-mini", "azure-gpt-4"],
    // "Reasoning (OpenAI)": ["o1", "o1-preview", "o1-mini", "o3", "o3-mini"],
    "Reasoning (OpenAI)": ["o1", "o3", "o3-mini"],
    // WJ CHANGE
    "Deep Research (OpenAI)": ["o3-deep-research", "o4-mini-deep-research"],
    // END OF WJ CHANGE
    // "GPT-4.5 (OpenAI)": ["gpt-4.5-preview"],
    "GPT-5.1 (OpenAI)": ["gpt-5.1", "gpt-5.1-codex", "gpt-5.1-codex-mini"],
    "GPT-5 (OpenAI)": ["gpt-5", "gpt-5-mini", "gpt-5-nano", "gpt-5-codex"],
    "GPT-4o (OpenAI)": ["gpt-4o", "gpt-4o-mini"],
    "GPT-4 (OpenAI)": ["gpt-4"],
    "GPT-4.1 (OpenAI)": ["gpt-4.1"],
    "Claude 4 (Anthropic)": ["claude-4-sonnet"],
    "Claude 3.7 (Anthropic)": ["claude-3.7-sonnet"],
    "Claude 3.5 (Anthropic)": ["claude-3.5-haiku", "claude-3.5-sonnet", "claude-3.5-sonnet-v2",],
    "Amazon Nova": ["nova-lite-v1"],
    "Gemini 2.5": ["gemini-2.5-pro", "gemini-2.5-flash", "gemini-2.5-flash-lite"],
    //"azure-o1-preview",
    // "GPT-3.5 (OpenAI)": ["gpt-3.5-turbo"],
    // "Claude AI": ["claude-3.5-sonnet"],
    // "Image Generation (OpenAI)": ["dall-e-2", "dall-e-3", "gpt-image-1"],
    "SEA-LION (AI Singapore)": [
      "sealion-v3.5-70B-R",
      "sealion-v3-70B-IT",
      "sealion-v4-27B-IT"
    ],
    "DeepSeek": [
      "deepseek-r1"
    ],
    "Perplexity AI": [
      "sonar",
      "sonar-pro",
      // "sonar-reasoning",
      "sonar-reasoning-pro",
      "sonar-deep-research"
    ],
    "GPT-OSS": ["gpt-oss-20b", "gpt-oss-120b"]
  };

  const handleEditImage = (image) => {
    setImageToEdit(image);
    setIsEditPopupOpen(true);
  };

  const isMobile = useIsMobile();

  // state for Apply indicator (optional)
  const [applied, setApplied] = useState(false);
  const editorRef = useRef(null);
  const fromTypingRef = useRef(false);
  const revertTimerRef = useRefAlias(null);
  const [draftPrompt, setDraftPrompt]   = useState(systemPrompt);
  const [dirty, setDirty]               = useState(false);


  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxSource, setLightboxSource] = useState("chat"); // "chat" | "composer"

  const [showMoreOptions, setShowMoreOptions] = useState(false);

  const moreMenuRef = useRef(null);
  const isAnyPopupOpen = isFilePopupOpen || isAudioPopupOpen || isPDFUploadOpen;

  useEffect(() => {
    const handleClickOutside = (event) => {
      // ⛔ If any popup is open, ignore outside clicks
      if (isAnyPopupOpen) return;

      if (!moreMenuRef.current) return;
      if (!moreMenuRef.current.contains(event.target)) {
        setShowMoreOptions(false);
      }
    };

    if (showMoreOptions) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMoreOptions, isAnyPopupOpen]);

  // Build gallery from chat history (for images already sent)
  const gallery = useMemo(() => {
    const out = [];
    chatHistory.forEach((msg, mIdx) => {
      if (Array.isArray(msg.content)) {
        msg.content.forEach((item, iIdx) => {
          if (item?.type === "image_url" && item.image_url) {
            out.push({ url: item.image_url, msgIndex: mIdx, itemIndex: iIdx });
          }
        });
      }
    });
    return out;
  }, [chatHistory]);

  // Map "mIdx:iIdx" -> index in gallery
  const galleryIndexByKey = useMemo(() => {
    const map = new Map();
    gallery.forEach((g, idx) => map.set(`${g.msgIndex}:${g.itemIndex}`, idx));
    return map;
  }, [gallery]);

  const openLightboxAt = useCallback((idx, source) => {
    setLightboxSource(source);        // "chat" or "composer"
    setLightboxIndex(idx);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    setLightboxIndex(0);
    document.body.style.overflow = "";
  }, []);

  const currentArray = lightboxSource === "composer" ? imagePreviews : gallery;
  const total = currentArray.length;

  const goPrev = useCallback(() => {
    setLightboxIndex((i) => (i - 1 + total) % total);
  }, [total]);

  const goNext = useCallback(() => {
    setLightboxIndex((i) => (i + 1) % total);
  }, [total]);

  // keyboard
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen, closeLightbox, goPrev, goNext]);

  // Convenience openers
  const openChatLightboxAt = useCallback((idx) => openLightboxAt(idx, "chat"), [openLightboxAt]);
  const openComposerLightboxAt = useCallback((idx) => openLightboxAt(idx, "composer"), [openLightboxAt]);





  useEffect(() => {
    // whenever the saved prompt changes externally, reset draft if not dirty
    if (dirty) return;
    setDraftPrompt(systemPrompt || "");
  }, [systemPrompt, dirty]);

  // Keep the editor DOM in sync with DRAFT (but don't stomp while typing)
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (fromTypingRef.current) { fromTypingRef.current = false; return; }
    if (el.textContent !== (draftPrompt || "")) {
      el.textContent = draftPrompt || "";
      // caret to end
      const r = document.createRange();
      r.selectNodeContents(el);
      r.collapse(false);
      const s = window.getSelection();
      s.removeAllRanges(); s.addRange(r);
    }
    el.classList.toggle("empty", !el.textContent);
  }, [draftPrompt]);

  const handleEditableInput = (e) => {
    fromTypingRef.current = true;
    const val = e.currentTarget.textContent || "";
    setDraftPrompt(val);
    setDirty(val !== (systemPrompt || ""));       // mark unsaved changes
    e.currentTarget.classList.toggle("empty", !val);
  };



  const handleApplySystemPrompt = () => {
    const text = (editorRef.current?.textContent || "").trim();
    setHasSavedSystemPrompt(true);
    setSystemPrompt(text || DEFAULT_SYSTEM_PROMPT);                        // save
    try { localStorage.setItem("systemPrompt", text); } catch {}
    setDirty(false);
    setApplied(true);
    // wait 2s before collapsing
    setTimeout(() => {
      setIsPromptVisible(false)
    }, 2000);
  };

  const insertChip = (t) => {
    const updated = (draftPrompt ? `${draftPrompt}\n${t} ` : `${t} `);
    setDraftPrompt(updated);
    setDirty(true);
    // push into DOM now & move caret
    requestAnimationFrame(() => {
      if (!editorRef.current) return;
      editorRef.current.textContent = updated;
      const r = document.createRange(); r.selectNodeContents(editorRef.current); r.collapse(false);
      const s = window.getSelection(); s.removeAllRanges(); s.addRange(r);
    });
  };


  // // Remove a file and update the extracted text
  const removeFile = async (fileName) => {

    if (isS3UploadEnabled) {
      // RAG mode
      const updatedUploadedFiles = uploadedFiles.filter(file => file.filename !== fileName);
      const updatedFileList = fileList.filter((file) => file.name !== fileName);
      const updatedImagePreviews = imagePreviews.filter(
        (preview) => preview.name !== fileName
      );

      setUploadedFiles(updatedUploadedFiles);
      setFileList(updatedFileList);
      setImagePreviews(updatedImagePreviews);

      // console.log(`Removed ${fileName} from RAG files. Remaining files:`, updatedUploadedFiles.length);

      // Optionally show a message
      setFileWarningMessage(`Removed ${fileName} from RAG files`);
      setTimeout(() => setFileWarningMessage(""), 2000);

    } else {
      // Context mode: Original behavior
      const updatedFileList = fileList.filter((file) => file.name !== fileName);
      const updatedImagePreviews = imagePreviews.filter(
        (preview) => preview.name !== fileName
      );

      // Recalculate the extracted text based on the remaining files
      const { combinedText } = await extractContentFromFiles(updatedFileList);

      setFileList(updatedFileList);
      setImagePreviews(updatedImagePreviews);
      setExtractedText(combinedText); // Update the state
    }
  };

  const isImg = (name = '') =>
    ['.png', '.jpeg', '.jpg', '.webp', '.gif'].some(ext => name.toLowerCase().endsWith(ext));

  const clearImagesOnly = () => {
    // 1) Remove images from the fileList (keep docs)
    setFileList(prev => prev.filter(f => !isImg(f.name)));

    // 2) Remove image previews
    setImagePreviews([]);

    // 3) Remove “direct” uploaded images and any uploaded entries that are images
    setUploadedFiles(prev =>
      prev.filter(f =>
        // keep if NOT a local image AND not an image filename
        !(f.localImage === true || isImg(f.filename || ''))
      )
    );

    // 4) If no images remain, clear any inpainting mask, too
    setMaskPreview(null);
    setMaskSavedMessage(false);
  };

  // thinking bubble is: { role: "assistant", content: "..." }
  const removeThinkingBubble = () => {
    setChatHistory(prev => {
      const next = [...prev];
      // find last assistant message whose content is exactly "..."
      const revIdx = [...next].reverse()
        .findIndex(m => m.role === "assistant" && m.content === "...");
      if (revIdx !== -1) {
        const idx = next.length - 1 - revIdx;
        next.splice(idx, 1); // remove that bubble
      }
      return next;
    });
  };



  // Add this function to your GenericAI component

  const generateGeminiImage = async (userPrompt, conversationHistory = [], imageFiles = [], setChatHistory) => {
    try {
      // console.log(`Generating image with Gemini 2.5 Flash Image model`);
      // console.log(`Prompt: ${userPrompt}`);

      // Add placeholder for loading state
      // const placeholderImageMessage = {
      //   role: "assistant",
      //   content: [{
      //     type: "text",
      //     text: "🎨 Generating image with Gemini...",
      //     streaming: true
      //   }],
      // };

      // setChatHistory((prev) => [...prev, placeholderImageMessage]);

      removeThinkingBubble(); // remove any existing "..." bubble


      const placeholderImageMessage = {
        role: "assistant",
        content: "...",
        imageGenerating: true
      };
      setChatHistory((prev) => [...prev, placeholderImageMessage]);

      // Prepare the conversation contents for Gemini
      const contents = [];

      // Add conversation history if available (for multi-turn conversations)
      if (conversationHistory.length > 0) {
        // Get last few turns for context (limit to avoid token limits)
        const recentHistory = conversationHistory.slice(-4); // Last 2 exchanges

        recentHistory.forEach(turn => {
          if (turn.role === "user") {
            // Simple text user message
            if (typeof turn.content === "string") {
              contents.push({
                role: "user",
                parts: [{ text: turn.content }]
              });
            }
            // Array content (text + images)
            else if (Array.isArray(turn.content)) {
              const parts = [];
              turn.content.forEach(item => {
                if (item.type === "text") {
                  parts.push({ text: item.text });
                }
              });
              if (parts.length > 0) {
                contents.push({ role: "user", parts });
              }
            }
          } else if (turn.role === "assistant") {
            // For assistant responses with images, include both text and image
            if (Array.isArray(turn.content)) {
              const parts = [];
              turn.content.forEach(item => {
                if (item.type === "text") {
                  parts.push({ text: item.text });
                } else if (item.type === "image_url" && item.image_url) {
                  // Convert the generated image back to inlineData format for Gemini
                  const imageUrl = item.image_url;
                  if (imageUrl.startsWith("data:image/")) {
                    const base64Data = imageUrl.split(",")[1];
                    if (base64Data) {
                      parts.push({
                        inlineData: {
                          mimeType: "image/png",
                          data: base64Data
                        }
                      });
                    }
                  }
                }
              });
              if (parts.length > 0) {
                contents.push({ role: "model", parts });
              }
            } else if (typeof turn.content === "string" && turn.content.includes("Generated image")) {
              contents.push({
                role: "model",
                parts: [{ text: turn.content }]
              });
            }
          }
        });
      }

      // Prepare current request parts
      const currentParts = [{ text: userPrompt }];

      // Add reference images if provided
      if (imageFiles.length > 0) {
        for (const file of imageFiles) {
          const fileType = file.name.endsWith(".jpg") ? "jpeg" : file.name.split(".").pop();
          const b64 = await convertImageToBase64(file);
          currentParts.push({
            inlineData: {
              mimeType: `image/${fileType}`,
              data: b64
            }
          });
        }
      }

      // Add current request
      contents.push({ role: "user", parts: currentParts });

      // Prepare the payload for Gemini lambda with contents format
      const payload = {
        model: "gemini-2.5-flash-image",
        contents: contents
      };

      const lambdaUrl = "https://wik67h2ozhuefrn43jh3giuuli0kzlvo.lambda-url.ap-southeast-1.on.aws/";

      // console.log("Sending Gemini image request:", JSON.stringify(payload, null, 2));

      const response = await fetch(lambdaUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Gemini Image API failed with status:", response.status);
        console.error("Error response:", errorText);
        throw new Error(`Gemini Image API failed: ${response.status}`);
      }

      const data = await response.json();
      // console.log("Gemini Image Response:", data);

      // Extract the generated image from the response - handle Lambda wrapper format
      let actualData = data;

      // Check if response is wrapped in Lambda format (statusCode, body)
      if (data.statusCode && data.body) {
        try {
          actualData = JSON.parse(data.body);
          // console.log("Parsed Lambda body:", actualData);
        } catch (e) {
          console.error("Failed to parse Lambda body:", e);
          actualData = data;
        }
      }

      let imageB64 = null;
      let responseText = "";

      // Try the updated lambda format first
      if (actualData.output && actualData.output[0] && actualData.output[0].content && actualData.output[0].content.parts) {
        for (const part of actualData.output[0].content.parts) {
          if (part.text) {
            responseText += part.text;
          }
          if (part.inlineData && part.inlineData.data) {
            imageB64 = part.inlineData.data;
          }
        }
      }
      // Try direct Gemini API format (candidates)
      else if (actualData.output && actualData.output[0] && actualData.output[0].content && actualData.output[0].content.parts) {
        // This handles the format: output[0] -> candidates format
        const candidates = actualData.output;
        if (candidates && candidates[0] && candidates[0].content && candidates[0].content.parts) {
          for (const part of candidates[0].content.parts) {
            if (part.text) {
              responseText += part.text;
            }
            if (part.inlineData && part.inlineData.data) {
              imageB64 = part.inlineData.data;
            }
          }
        }
      }
      // Try direct candidates format
      else if (actualData.candidates && actualData.candidates[0] && actualData.candidates[0].content && actualData.candidates[0].content.parts) {
        for (const part of actualData.candidates[0].content.parts) {
          if (part.text) {
            responseText += part.text;
          }
          if (part.inlineData && part.inlineData.data) {
            imageB64 = part.inlineData.data;
          }
        }
      }

      // console.log("Parsed image B64 length:", imageB64 ? imageB64.length : "null");
      // console.log("Response text:", responseText);

      if (!imageB64) {
        console.error("Full response structure:", JSON.stringify(data, null, 2));
        throw new Error("No image data received from Gemini");
      }

      // Convert to data URL
      const imageUrl = `data:image/png;base64,${imageB64}`;

      return {
        imageUrl,
        responseText: responseText || `Generated image for: "${userPrompt}"`
      };

    } catch (error) {
      console.error("Error in generateGeminiImage:", error);

      // Update the loading message with error
      setChatHistory((prev) => {
        const newHistory = [...prev];
        const lastMessage = newHistory[newHistory.length - 1];

        if (
          // lastMessage &&
          // lastMessage.role === "assistant" &&
          // Array.isArray(lastMessage.content) &&
          // lastMessage.content[0] &&
          // lastMessage.content[0].text === "🎨 Generating image with Gemini..." &&
          // lastMessage.content[0].streaming === true
          lastMessage &&
          lastMessage.role === "assistant" &&

          lastMessage.imageGenerating === true &&
          lastMessage.content === "..."
        ) {
          // Replace the placeholder with an error message
          lastMessage.content = [
            {
              type: "text",
              text: error.message.includes("safety system")
                ? `⚠️ Image blocked by moderation: ${error.message}`
                : `❌ Gemini image generation failed: ${error.message || "Unknown error"}. Try again.`
            }
          ];
          return newHistory;
        }

        // If no placeholder found, just append an error message
        newHistory.push({
          role: "assistant",
          content: [{ type: "text", text: `❌ Gemini image generation failed: ${error.message || "Unknown error"}` }]
        });
        return newHistory;
      });

      throw error;
    }
  };

  const generateImage = async (imageModel, userPrompt, conversationSummary, isRefined) => {
    // removeThinkingBubble();
    let lambdaUrl = "https://2zluuy74fgsan7isxt3pdflrlu0ysjth.lambda-url.ap-southeast-1.on.aws/";


    const chosenModel = IMAGE_CAPABLE_MODELS.has(imageModel)
      ? imageModel
      : "gpt-5";

    // console.log(`Chosen Model for Image Generation: ${chosenModel}`);
    // console.log(`Final Prompt: ${userPrompt}`);

    const payload = {
      // model: "gpt-image-1",
      // model: imageModel,
      // prompt: "Generate a picture of the prompt, even if it is not descriptive. Prompt: " + (userPrompt || ""),
      model: chosenModel,
      prompt: userPrompt
    };


    // console.log(payload)

    // Console log the previous response ID check
    if (imageResponseId) {
      console.log("Previous response ID found:", imageResponseId);
      payload.previous_response_id = imageResponseId;
    }
    //  else {
    //   console.log("No previous response ID - this is a new conversation");
    // }

    // Console log the complete payload before sending
    // console.log("Image generation payload:", JSON.stringify(payload, null, 2));

    const imageFiles = fileList?.filter((f) => /\.(png|jpe?g|webp)$/i.test(f.name)) || [];

    if (imageFiles.length) {
      const images = await Promise.all(
        imageFiles.map(async (file) => {
          const fileType = file.name.endsWith(".jpg") ? "jpeg" : file.name.split(".").pop();
          const b64 = await convertImageToBase64(file);
          const imagePayload = {
            type: `image/${fileType}`,
            image: `data:image/${fileType};base64,${b64}`,
          };
          return imagePayload;
        })
      );
      payload.image = images;

      if (maskPreview) {
        payload.mask = maskPreview;
      }
    }

    // Console log final payload after adding images/mask
    // console.log("Final payload with images/mask:", JSON.stringify(payload, null, 2));

    try {
      const response = await fetch(lambdaUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Image API failed with status:", response.status);
        console.error("Error response:", errorText);
        throw new Error(`Image API failed: ${response.status}`);
      }

      // setFileList([]);
      // setExtractedText("");
      clearImagesOnly();
      setImagePreviews([]);

      // Handle streaming response from streamifyResponse Lambda
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finalImageUrl = null;
      let lastErrorCode = null;

      
      removeThinkingBubble();

      // Placeholder for streaming image
      // const placeholderImageMessage = {
      //   role: "assistant",
      //   content: [{
      //     type: "text",
      //     text: "🎨 Generating image...",
      //     streaming: true
      //   }],
      // };

      // Placeholder for streaming image
      const placeholderImageMessage = {
        role: "assistant",
        content: "...",
        imageGenerating: true
      };
      setChatHistory((prev) => [...prev, placeholderImageMessage]);

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          // Process complete lines from buffer
          const lines = buffer.split('\n');
          buffer = lines.pop() || ""; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.trim()) {
              try {
                const data = JSON.parse(line);
                // console.log("Received streaming data:", data);

                if (data.type === "partial_image") {
                  // console.log("Partial image received, index:", data.partial_image_index);
                  // Update the message to show the partial image
                  const latestPartialUrl = `data:image/png;base64,${data.partial_image_b64}`;
                  setChatHistory((prev) => {
                    const newHistory = [...prev];
                    const lastMessage = newHistory[newHistory.length - 1];
                    if (lastMessage.role === "assistant") {
                      // Replace the generating text with the partial image
                      lastMessage.content = [{
                        type: "image_url",
                        image_url: latestPartialUrl,
                        streaming: true
                      }];
                    }
                    return newHistory;
                  });

                  // Store as potential final image
                  finalImageUrl = latestPartialUrl;

                } else if (data.type === "complete") {
                  // Set response ID for follow-up requests
                  if (data.response_id) {
                    // console.log("NEW RESPONSE ID RECEIVED:", data.response_id);
                    // console.log("Previous imageResponseId was:", imageResponseId);
                    setImageResponseId(data.response_id);

                    // Update visual state
                    if (userPrompt) {
                      setVisualState((prev) => {
                        if (!prev) return userPrompt; // first image
                        return `${userPrompt}`;       // overwrite with refined description
                      });
                    }


                    // console.log("Updated imageResponseId to:", data.response_id);
                  } else {
                    console.log("No response ID in completion data");
                  }

                  // Mark final image as complete
                  setChatHistory((prev) => {
                    const newHistory = [...prev];
                    const lastMessage = newHistory[newHistory.length - 1];
                    if (lastMessage.role === "assistant" &&
                      lastMessage.content[0] &&
                      lastMessage.content[0].type === "image_url") {
                      if (isRefined) {
                        lastMessage.content = [
                          { type: "text", text: `🖼️ Generated image for: "${userPrompt}"` },
                          { type: "image_url", image_url: finalImageUrl, streaming: false }
                        ];
                      } else {
                        lastMessage.content[0].streaming = false;
                      }
                    }
                    return newHistory;
                  });

                } else if (data.type === "debug") {
                  // Log debug information from Lambda
                  // console.log("🔍 Lambda Debug:", data.message);
                  // if (data.payload) {
                  //   console.log("📦 Payload sent to OpenAI:", data.payload);
                  // }
                  // if (data.chunkData) {
                  //   console.log("📥 OpenAI Chunk:", data.chunkData);
                  // }

                } else if (data.type === "error") {
                  // Error Handling:
                  
                  // if (data.errorCode.includes("moderation")) {
                  //   setChatHistory
                  // }

                  console.error("❌ Lambda Error Details:");
                  console.error("Error:", data.error);
                  console.error("Error Name:", data.errorName);
                  console.error("Error Code:", data.errorCode);
                  console.error("Timestamp:", data.timestamp);
                  console.error("Stack Trace:", data.errorStack);

                  if (data.openaiError) {
                    console.error("🤖 OpenAI API Error:");
                    console.error("Status:", data.openaiError.status);
                    console.error("Status Text:", data.openaiError.statusText);
                    console.error("Response Data:", data.openaiError.data);
                  }

                  if (data.requestError) {
                    console.error("🌐 Request Error:", data.requestError);
                  }


                  if (data.errorCode && data.errorCode.includes("moderation")) {
                    const msg =
                      "I wasn't able to generate that image because this request violates our content policies.\n\n" +
                      "No worries though — just send me a new idea for an image or tweak your prompt.";

                    // userPrompt here is the 2nd argument passed into generateImage(...)
                    // showAssistantError will also remove the `...` / placeholder bubble
                    showAssistantError(msg, userPrompt, { allowRegenerate: false });

                    // Tell caller “no image + moderation error” so it can skip generic message
                    return { imageUrl: null, errorCode: data.errorCode };
                  }

                  // other errors still go to the catch() handler
                  throw new Error(data.error);
                                  
                }

              } catch (parseError) {
                console.error("Error parsing streaming data:", parseError, "Line:", line);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      // console.log("Image generation completed. Final image URL:", finalImageUrl ? "Generated" : "None");
      // console.log("Current imageResponseId after generation:", imageResponseId);
      return { imageUrl: finalImageUrl, errorCode: lastErrorCode };

    } catch (error) {
      console.error("Error in generateImage function:", error);

      setChatHistory((prev) => {
        const newHistory = [...prev];
        const lastMessage = newHistory[newHistory.length - 1];

        if (
          lastMessage &&
          lastMessage.role === "assistant" &&

          lastMessage.imageGenerating === true &&
          lastMessage.content === "..."
          // Array.isArray(lastMessage.content) &&
          // lastMessage.content[0] &&
          // lastMessage.content.some(
          //   (c) =>
          //     c.streaming === true ||
          //     c.text === "🎨 Generating image..." ||
          //     c.type === "image_url"
          // )
        ) {
          // Replace the placeholder with an error message
          lastMessage.content = [
            {
              type: "text",
              text: error.message.includes("safety system")
                ? `⚠️ Image blocked by moderation: ${error.message}`
                : `❌ Image generation failed: ${error.message || "Unknown error"}. Try again.`
            }
          ];
          return newHistory;
        }

        // If no placeholder found, just append an error message
        newHistory.push({
          role: "assistant",
          content: [{ type: "text", text: `❌ Image generation failed: ${error.message || "Unknown error"}` }]
        });
        return newHistory;
      });

      throw error;
    }
  };

  // Helper function to process conversation history
  // const processConversationHistory = (chatHistory) => {
  //   if (chatHistory.length === 0) return "";
  //   const chatHistorySlice = chatHistory.slice(
  //     Math.max(chatHistory.length - 40, 0)
  //   );

  //   return chatHistorySlice
  //     .map((message) => {
  //       if (!message.content) return ""; // Safeguard for undefined content
  //       const contentString = Array.isArray(message.content)
  //         ? message.content
  //           .map((item) => {
  //             if (item.type === "text") {
  //               return item.text;
  //             } else if (item.type === "image_url") {
  //               return "Uploaded Image: [Reference the uploaded image here]";
  //             }
  //             return "";
  //           })
  //           .join(" ")
  //         : message.content;

  //       return `${message.role === "user"
  //         ? "{role: user, content: "
  //         : "{role: assistant, content: "
  //         }${contentString}},`;
  //     })
  //     .join("\n");
  // };
  const processConversationHistory = (chatHistory) => {
    if (!Array.isArray(chatHistory) || chatHistory.length === 0) return "";

    const slice = chatHistory.slice(Math.max(chatHistory.length - 40, 0));

    const lines = slice.map((m) => {
      if (!m?.content) return "";

      const text =
        Array.isArray(m.content)
          ? m.content
              .map((p) => (p.type === "text" ? p.text : p.type === "image_url" ? "[Image]" : ""))
              .join(" ")
          : typeof m.content === "string"
            ? m.content
            : (m.content.type === "text" ? m.content.text : "");

      const role = m.role === "user" ? "User" : "Assistant";
      return text ? `${role}: ${text}` : "";
    });

    return lines.filter(Boolean).join("\n");
  };



  const showAssistantError = (message, regeneratePrompt, options = {}) => {
  const { allowRegenerate = true } = options;

  setChatHistory(prev => {
    const history = [...prev];

    // If the last assistant message is just a placeholder, remove it
    if (history.length) {
      const last = history[history.length - 1];
      if (
        last.role === "assistant" &&
        (last.content === "..." || last.content === "")
      ) {
        history.pop();
      }
    }

    // If caller didn't pass a prompt, fall back to last user text
    let prompt = regeneratePrompt || null;

    if (!prompt && allowRegenerate) {
      for (let i = history.length - 1; i >= 0; i--) {
        const msg = history[i];
        if (msg.role !== "user") continue;

        if (Array.isArray(msg.content)) {
          const textPart = msg.content.find(
            p => p.type === "text" && p.text
          );
          if (textPart) {
            prompt = textPart.text;
            break;
          }
        } else if (typeof msg.content === "string") {
          prompt = msg.content;
          break;
        }
      }
    }

    history.push({
      role: "assistant",
      content: message,
      error: true,
      regeneratePrompt: allowRegenerate && prompt ? prompt : null,
    });

    return history;
  });
};



  const handleRegenerate = (prompt) => {
    if (!prompt || loading) return;

    // Keep the textarea as-is (usually empty) and re-send the old prompt internally
    handleSendPrompt(prompt, { clearComposer: false });
  };


  const handleSendPrompt = async (overridePrompt=undefined, options={}) => {
    // Require both prompts
    // if (!systemPrompt.trim() || !userPrompt.trim()) {
    //   setWarningMessage("System Prompt and User Prompt should not be empty.");
    //   setTimeout(() => setWarningMessage(""), 3000);
    //   return;
    // }
    // setWarningMessage("");

    // if (loading) return;

    // setLoading(true);

    // // 🔹 Capture current user prompt BEFORE clearing
    // const currentUserPrompt = userPrompt;
    // setUserPrompt("");
    // requestAnimationFrame(resetTextArea);

    // let assistantResponse = "";
    // let conversationSummary = "";

    // let content = [];

    // Use override if provided, otherwise fall back to textarea state
    const promptToUse = overridePrompt ?? userPrompt;

      // If caller didn't say otherwise, clear composer only for "normal" sends
    const { clearComposer = overridePrompt == null } = options;

    // 🚫 Block empty prompts for both normal sends + regenerate
    const trimmedPrompt = 
      typeof promptToUse === "string" ? promptToUse.trim() : "";

    if (!trimmedPrompt) {
      setWarningMessage("Please enter a message before sending.");
      setTimeout(() => setWarningMessage(""), 3000);
      return;
    }


    if (!systemPrompt.trim()) {
      setWarningMessage("System Prompt should not be empty.");
      setTimeout(() => setWarningMessage(""), 3000);
      return;
    }

    if (loading) return;

    setLoading(true);

    // 🔹 Capture prompt that will actually be sent
    const currentUserPrompt = trimmedPrompt;

    // Only clear textarea for normal sends
    if (clearComposer) {
      setUserPrompt("");
      requestAnimationFrame(resetTextArea);
    }

    let assistantResponse = "";
    let conversationSummary = "";

    let content = [];

    if (!isS3UploadEnabled) {
      // for RAG mode
      if (uploadMode === "context" && isDirectUploadModelSelected) {
        const imageFiles = fileList.filter((file) => isImageFile(file.name));
        if (imageFiles.length > 0) {
          const imageContent = await extractContentFromFiles(imageFiles);
          content = imageContent.content || [];
        }
      } else {
        const extractedContent = await extractContentFromFiles(fileList);
        // console.log("Extracted content from files:", extractedContent);
        content = extractedContent.content || [];
      }
    }

    // console.log(`content: ${JSON.stringify(content)}`);

    // 🔹 Build user message content for chat bubble (text + files + images)
    const messageContent = [];

    if (currentUserPrompt) {
      messageContent.push({ type: "text", text: currentUserPrompt });
    }

    // images (as before)
    messageContent.push(
      ...content
        .filter((file) => file.image_url)
        .map((file) => ({
          type: "image_url",
          image_url: file.image_url,
        }))
    );

    // non-image files as attachments for chat history
    const nonImageFiles = fileList.filter((file) => {
      const name = file?.name || "";
      const type = file?.type || "";
      const isImage =
        type.startsWith("image/") ||
        /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
      return !isImage;
    });

    nonImageFiles.forEach((file) => {
      const uploadedFile = uploadedFiles.find(
        (u) => u.filename === file.name
      );
      messageContent.push({
        type: "file",
        name: file.name,
        mime: file.type,
        fileId: uploadedFile?.fileId,
        status: uploadedFile?.status,
      });
    });

    const userMessage = {
      role: "user",
      content: messageContent.length
        ? messageContent
        : [{ type: "text", text: currentUserPrompt }],
    };

    setChatHistory((prev) => [...prev, userMessage]);

    setChatHistory(prev => [...prev, { role: "assistant", content: "..." }]);

    // 🔹 After sending: remove non-image files from composer pills
    setFileList((prev) =>
      prev.filter((file) => {
        const name = file?.name || "";
        const type = file?.type || "";
        const isImage =
          type.startsWith("image/") ||
          /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
        return isImage; // keep only images (for your existing image preview flow)
      })
    );



    // 🔹 Reset only visual/image stuff (keep uploadedFiles for RAG/context)
    if (!isS3UploadEnabled && uploadMode !== "direct") {
      clearImagesOnly();
      setImagePreviews([]);
      setMaskPreview(null);
      setMaskSavedMessage(false);
    }

    // Conversation summary (unchanged)
    if (chatHistory.length > 0) {
      const promptHistory = chatHistory.filter(
        m => !m.excludeFromPrompt && !m.isStatusMessage
      );
      // console.log("Processing conversation history for summary:", promptHistory);

      conversationSummary = processConversationHistory(promptHistory);
    }

    // ---------- RAG + context building (uses currentUserPrompt) ----------
    let ragContext = "";
    let ragMetadata = null;
    let ragData = null;

    if (isS3UploadEnabled && uploadedFiles.length > 0) {
      // RAG mode
      // const searchingMessage = {
      //   role: "assistant",
      //   content: "🔍 Searching uploaded documents for relevant information...",
      // };
      // setChatHistory((prev) => [...prev, searchingMessage]);

      try {
        const ragResult = await queryRAGFiles(
          currentUserPrompt,
          uploadedFiles
        );
        ragData = ragResult;

        if (ragResult.ragMetadata.belowThreshold) {
          setChatHistory((prev) => [
            ...prev.slice(0, -1),
            {
              role: "assistant",
              content:
                `No relevant information found in RAG documents (max similarity: ${ragResult.ragMetadata.maxSimilarity.toFixed(
                  3
                )}). ` +
                "Processing documents in context mode instead...",
              isStatusMessage: true,
            },
          ]);

          const filesToProcess = fileList.filter((file) =>
            uploadedFiles.some(
              (uploaded) => uploaded.filename === file.name
            )
          );

          if (filesToProcess.length > 0) {
            const { combinedText } =
              await extractContentFromFiles(filesToProcess);
            ragContext = combinedText
              ? `\n\nFallback Document Context (full document processing):\n${combinedText}\n\nNote: This is the full document content as RAG retrieval found no relevant sections.`
              : "";

            setChatHistory((prev) => [
              ...prev.slice(0, -1),
              {
                role: "assistant",
                content:
                  `No relevant information found in RAG documents. ` +
                  `Processed ${filesToProcess.length} document(s) in full context mode instead.`,
                isStatusMessage: true,
              },
            ]);
          } else {
            ragContext = "";
          }
        } else {
          ragContext = ragResult.relevantChunks
            .map(
              (chunk) =>
                `[Source: ${chunk.source} | Similarity: ${(chunk.similarity * 100).toFixed(
                  1
                )}%]\n${chunk.text}`
            )
            .join("\n\n---\n\n");

          ragMetadata = ragResult.ragMetadata;
          setChatHistory((prev) => prev.slice(0, -1));
        }
      } catch (error) {
        console.error("RAG query failed:", error);
        setChatHistory((prev) => [
          ...prev.slice(0, -1),
          {
            role: "assistant",
            content:
              "RAG query failed, processing documents in context mode instead...",
            isStatusMessage: true,
          },
        ]);

        setWarningMessage(
          "RAG query failed, processing documents in context mode instead."
        );
        setTimeout(() => setWarningMessage(""), 3000);

        const filesToProcess = fileList.filter((file) =>
          uploadedFiles.some(
            (uploaded) => uploaded.filename === file.name
          )
        );

        if (filesToProcess.length > 0) {
          const { combinedText } =
            await extractContentFromFiles(filesToProcess);
          ragContext = combinedText
            ? `\n\nFallback Document Context (due to RAG error):\n${combinedText}`
            : "";

          setChatHistory((prev) => [
            ...prev.slice(0, -1),
            {
              role: "assistant",
              content:
                `RAG query failed. Processed ${filesToProcess.length} document(s) in context mode instead.`,
              isStatusMessage: true,
            },
          ]);
        }
      }
    }

    const fileContext =
      !isS3UploadEnabled && (extractedText || content.length > 0)
        ? `\n\nContext from Uploaded Files:\n` +
          (extractedText ? `${extractedText}\n` : "") +
          content
            .map((file) =>
              file.type === "input_image"
                ? `Uploaded Image: [Reference the uploaded image here]`
                : ""
            )
            .join("\n")
        : "";


    const ragContextText = ragContext
      ? `\n\nRelevant Document Context (answer based on this information):\n${ragContext}\n\nIMPORTANT: Base your answer primarily on the document context above. If the context doesn't contain sufficient information, state that clearly and mention what information is missing.`
      : "";

    const contextToUse = isS3UploadEnabled ? ragContextText : fileContext;

    // 🔹 Use currentUserPrompt (not cleared state) for enhanced prompt
    // console.log("Context to use in prompt:", contextToUse);
    // console.log(`content: ${currentUserPrompt}${contextToUse}`);
    // console.log(`conversationSummary: ${conversationSummary}`);

    // const enhancedUserPrompt = conversationSummary
    //   ? `${conversationSummary} {role: user, content: ${currentUserPrompt}${contextToUse}}`
    //   : `${currentUserPrompt}${contextToUse}`;

    const enhancedUserPrompt = conversationSummary
      ? `${conversationSummary}\n\nUser: ${currentUserPrompt}${contextToUse || ""}\nAssistant:`
      : `${currentUserPrompt}${contextToUse || ""}`;

      // console.log("Enhanced User Prompt:", enhancedUserPrompt);


    // console.log('enhancedUserPrompt', enhancedUserPrompt);
    // console.log('userPrompt', userPrompt);

    try {
      let lambdaUrl;
      let payload;

      if (selectedModel.startsWith("azure-")) {
        // Azure streaming endpoint
        const modelName = selectedModel.replace("azure-", "");

        // Define a mapping of model names to their respective lambda URLs and additional configurations
        const azureModelConfig = {
          "o1-mini": {
            lambdaUrl:
              "https://7zjhepnjryffab7s3yew5mnxoi0umjfp.lambda-url.ap-southeast-1.on.aws/",
            payload: {
              azure_model_endpoint: modelName,
              messages: [{ role: "user", content: enhancedUserPrompt }],
            },
          },
          o1: {
            lambdaUrl:
              "https://7zjhepnjryffab7s3yew5mnxoi0umjfp.lambda-url.ap-southeast-1.on.aws/",
            payload: {
              azure_model_endpoint: modelName,
              messages: [
                { role: "system", content: systemPrompt },
                { role: "user", content: enhancedUserPrompt },
              ],
              reasoning_effort: reasoningEffort,
            },
          },
          "o3-mini": {
            lambdaUrl:
              "https://j3fzcjerdlodtmp45jaqdeh6hi0cbhdo.lambda-url.ap-southeast-1.on.aws/",
            payload: {
              azure_model_endpoint: modelName,
              messages: [
                { role: "system", content: systemPrompt },
                { role: "user", content: enhancedUserPrompt },
              ],
              reasoning_effort: reasoningEffort,
            },
          },
          default: {
            lambdaUrl:
              "https://j3fzcjerdlodtmp45jaqdeh6hi0cbhdo.lambda-url.ap-southeast-1.on.aws/",
            payload: {
              azure_model_endpoint: modelName,
              messages: [
                { role: "system", content: systemPrompt },
                { role: "user", content: enhancedUserPrompt },
              ],
            },
          },
        };

        // Use the mapping to set the lambdaUrl and payload
        const config = azureModelConfig[modelName] || azureModelConfig.default;
        lambdaUrl = config.lambdaUrl;
        payload = config.payload;
      }

      // Determine the appropriate Lambda function based on the selected model
      else if (["o1", "o3", "o3-mini"].includes(selectedModel)) {
        // Define a mapping of models to their respective lambda URLs
        const modelConfig = {
          o1: {
            lambdaUrl:
              "https://keav6fptwkwfj5zrqolbqydelq0htpng.lambda-url.ap-southeast-1.on.aws/",
          },
          o3: {
            lambdaUrl:
              "https://keav6fptwkwfj5zrqolbqydelq0htpng.lambda-url.ap-southeast-1.on.aws/",
          },
          "o3-mini": {
            lambdaUrl:
              "https://qipgrgsrzr57wb3lvmpjjwuqy40wyxwl.lambda-url.ap-southeast-1.on.aws/",
          },
        };

        // Map the selected model to its corresponding lambda URL and payload
        const config = modelConfig[selectedModel];
        lambdaUrl = config.lambdaUrl;
        payload = {
          model: selectedModel,
          systemPrompt,
          enhancedUserPrompt,
          reasoningEffort,
        };
      } else if (selectedModel.startsWith("claude-") || (selectedModel.startsWith("nova-"))) {
        // bedrock: claude and nova streaming endpoint
        const modelName = selectedModel.replace("claude-", "");

        // Universal lambdaUrl for all, pulls using model name
        lambdaUrl =
          "https://3u54y2lyqjhzck6rq6memyokay0jbrmo.lambda-url.ap-southeast-1.on.aws/";

        // Define a mapping of model names to their specific configurations
        const models = {
          "3.7-sonnet": "us.anthropic.claude-3-7-sonnet-20250219-v1:0",
          "3.5-haiku": "us.anthropic.claude-3-5-haiku-20241022-v1:0",
          "3.5-sonnet": "us.anthropic.claude-3-5-sonnet-20240620-v1:0",
          "3.5-sonnet-v2": "us.anthropic.claude-3-5-sonnet-20241022-v2:0",
          "4-sonnet": "us.anthropic.claude-sonnet-4-20250514-v1:0",
          "nova-lite-v1": "us.amazon.nova-lite-v1:0"
        };

        // Generate the payload dynamically
        if (models[modelName]) {
          payload = {
            model: models[modelName],
            max_tokens: 1000,
            system: systemPrompt,
            user_input: enhancedUserPrompt,
          };
        }
      } else if (selectedModel.startsWith("gemini-")) {
        lambdaUrl = "https://wik67h2ozhuefrn43jh3giuuli0kzlvo.lambda-url.ap-southeast-1.on.aws/";

        const historyMsgs = toTextOnlyMessages(chatHistory, 20);

        // Add current user text + any file/RAG context (already built as `contextToUse`)
        const userText = `${currentUserPrompt}${contextToUse || ""}`.trim();

        const messages = [
          { role: "system", content: [{ type: "text", text: systemPrompt }] },
          ...historyMsgs,
          { role: "user", content: [{ type: "text", text: userText }] }
        ];

        payload = { model: selectedModel, messages };
      }

      else if (selectedModel.startsWith("sealion-")) {
        lambdaUrl = "https://uge4yvc5v6qq3wpwy3d4rwn6hy0cwors.lambda-url.ap-southeast-1.on.aws/";

        const modelMapping = {
          "sealion-v3.5-70B-R": "aisingapore/Llama-SEA-LION-v3.5-70B-R",
          "sealion-v3-70B-IT": "aisingapore/Llama-SEA-LION-v3-70B-IT",
          "sealion-v3-8B-IT": "aisingapore/Gemma-SEA-LION-v3-9B-IT",
          "sealion-v3.5-8B-R": "aisingapore/Llama-SEA-LION-v3.5-8B-R"
        }

        const actualModelId = modelMapping[selectedModel] || selectedModel;

        payload = {
          model: actualModelId,
          messages: [
            { role: "system", content: systemPrompt },
            { role: "user", content: enhancedUserPrompt }
          ],
          max_tokens: 1000,
          temperature: 0.7
        };
      }
      else if (selectedModel.startsWith('deepseek-')) {
        lambdaUrl = "https://xji6ujhm6qmcfaxk7c3kfpcol40sflvm.lambda-url.ap-southeast-1.on.aws/";

        payload = {
          prompt: enhancedUserPrompt,
          model: selectedModel
        };
      }
      else if (selectedModel === "gpt-5-codex" || selectedModel === "gpt-5.1-codex" || selectedModel === "gpt-5.1-codex-mini" || selectedModel === "gpt-5.1") {
        lambdaUrl = "https://2xzetkss4nybfugbl4oufeuaje0txcdv.lambda-url.ap-southeast-1.on.aws/";

        let responseInput;

        if (content.length > 0) {
          // Images present: array format
          const contentArray = [
            { type: "input_text", text: enhancedUserPrompt }  // ✅ Changed from "text" to "input_text"
          ];

          content.forEach(file => {
            if (file.image_url) {
              contentArray.push({
                type: "input_image",  // ✅ Changed from "image_url" to "input_image"
                image_url: file.image_url
              });
            }
          });

          responseInput = [
            {
              type: "message",
              role: "user",
              content: contentArray
            }
          ];
        } else {
          responseInput = enhancedUserPrompt;
        }

        payload = {
          model: selectedModel,
          systemPrompt,
          input: responseInput,
        };
      }
      else if (
        selectedModel === "gpt-5" ||
        selectedModel === "gpt-5-mini" ||
        selectedModel === "gpt-5-nano"
      ) {
        const messages = [
          { role: "system", content: systemPrompt },
          ...(conversationSummary
            ? [
              {
                role: "system",
                content: conversationSummary,
              }
            ]
            : []),
          {
            role: "user",
            content: [
              { type: "text", text: enhancedUserPrompt },
              ...content.map((file) => ({
                type: "image_url",
                image_url: { url: file.image_url },
              })),
            ],
          },
        ];

        const userMessageIndex = messages.length - 1;

        // Add Direct API files to the message content
        if (uploadMode === 'context' && isDirectUploadModelSelected && uploadedFiles.length > 0) {
          const directApiFiles = uploadedFiles.filter(file =>
            file.provider === 'openai' && file.fileId && !file.localImage
          );

          if (directApiFiles.length > 0) {
            // console.log(`Adding ${directApiFiles.length} files to message`);

            // Add file objects to the user message content
            directApiFiles.forEach(file => {
              messages[userMessageIndex].content.push({
                type: "file",
                file: {
                  file_id: file.fileId,
                }
              });
            });
          }
        }

        lambdaUrl = "https://mejku5sat6kok3o2mydndbirg40azgvh.lambda-url.ap-southeast-1.on.aws/";
        payload = {
          model: selectedModel,
          messages,
        };
      }

      else if (
        selectedModel === "gpt-4o" || selectedModel === "gpt-4o-mini"
      ) {
        // Construct the messages array with conversation summary for history
        const messages = [
          { role: "system", content: systemPrompt },
          ...(conversationSummary
            ? [
              {
                role: "system",
                content: conversationSummary,
              }
            ]
            : []),
          {
            role: "user",
            content: [
              { type: "text", text: enhancedUserPrompt },
              ...content.map((file) => ({
                type: "image_url",
                image_url: { url: file.image_url },
              })),
            ],
          },
        ];

        const userMessageIndex = messages.length - 1;

        // Add Direct API files to the message content
        if (uploadMode === 'context' && isDirectUploadModelSelected && uploadedFiles.length > 0) {
          const directApiFiles = uploadedFiles.filter(file =>
            file.provider === 'openai' && file.fileId && !file.localImage
          );

          if (directApiFiles.length > 0) {
            // console.log(`Adding ${directApiFiles.length} files to message`);

            // Add file objects to the user message content
            directApiFiles.forEach(file => {
              messages[userMessageIndex].content.push({
                type: "file",
                file: {
                  file_id: file.fileId,
                }
              });
            });
          }
        }

        // Set the Lambda URL and payload
        lambdaUrl =
          "https://mejku5sat6kok3o2mydndbirg40azgvh.lambda-url.ap-southeast-1.on.aws/";
        payload = {
          model: selectedModel,
          messages, // Use the constructed messages array
        };

        // console.log("Enhanced user prompt:", enhancedUserPrompt);

      } else if (selectedModel.startsWith("sonar")) {
        // Perplexity AI endpoint
        lambdaUrl = "https://zkm4ckouspeofiqlr6a5bgrz3m0lmwyk.lambda-url.ap-southeast-1.on.aws/";

        const messages = [
          { role: "system", content: systemPrompt },
          { role: "user", content: enhancedUserPrompt }
        ];

        payload = {
          model: selectedModel,
          messages: messages,
          max_tokens: 4000,
          temperature: 0.2
        };

      }
      else if (selectedModel.startsWith("gpt-oss-")) {
        lambdaUrl = "https://7ch2vcujikhex6ixqfwgspbjvy0naudr.lambda-url.ap-southeast-1.on.aws/";

        const gptOssSystemPrompt = `${systemPrompt}\n\nReasoning: ${gptOssReasoning}`;
        payload = {
          prompt: enhancedUserPrompt,
          system_prompt: gptOssSystemPrompt,
          model: selectedModel
        };
      }

      // WJ CHANGE
      else if ((selectedModel.startsWith("o3-deep-research") || selectedModel.startsWith("o4-mini-deep-research"))
        && !enableImageGen
      ) {
        setInfoMessage("Deep Research initiated - this advanced analysis may take several minutes");
        setTimeout(() => setInfoMessage(""), 10000);

        lambdaUrl = "https://ofvmz63qifkrjqyc2fhmcuugfe0livoe.lambda-url.ap-southeast-1.on.aws/";

        const deepResearchModelMap = {
          "o3-deep-research": "o3-deep-research-2025-06-26",
          "o4-mini-deep-research": "o4-mini-deep-research-2025-06-26"
        };

        const actualModelId = deepResearchModelMap[selectedModel] || selectedModel;

        payload = {
          model: actualModelId,
          input: [
            {
              role: "developer",
              content: [
                {
                  type: "input_text",
                  text: systemPrompt
                }
              ]
            },
            {
              role: "user",
              content: [
                {
                  type: "input_text",
                  text: enhancedUserPrompt
                }
              ]
            }
          ],
          reasoning: {
            summary: "auto"
          },
          tools: [
            {
              type: "web_search_preview"
            }
          ]
        };

        // Start background Deep Research request
        try {
          const response = await fetch(lambdaUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          const responseId = data.id; // OpenAI returns an ID for background tasks

          if (!responseId) {
            throw new Error('No response ID received from Deep Research API');
          }

          removeThinkingBubble();

          // Add placeholder message with progress indicator
          setChatHistory((prev) => [...prev, {
            role: "assistant",
            content: "...",
            responseId: responseId,
            isProcessing: true
          }]);

          // Poll for completion
          const pollForCompletion = async () => {
            try {
              const pollResponse = await fetch(`${lambdaUrl}?action=poll&responseId=${responseId}`);

              if (!pollResponse.ok) {
                throw new Error(`Polling failed: ${pollResponse.status}`);
              }

              const pollData = await pollResponse.json();

              if (pollData.status === 'completed') {
                // Validate response structure
                if (!pollData.output || !Array.isArray(pollData.output) || pollData.output.length === 0) {
                  throw new Error('No research output received from Deep Research API');
                }

                const finalOutput = pollData.output[pollData.output.length - 1];

                if (!finalOutput || !finalOutput.content || !Array.isArray(finalOutput.content) || finalOutput.content.length === 0) {
                  throw new Error('Invalid research output format');
                }

                if (!finalOutput.content[0] || typeof finalOutput.content[0].text !== 'string') {
                  throw new Error('No text content found in research output');
                }

                const content = finalOutput.content[0].text;

                setLoading(false);
                // Update the message with results
                setChatHistory((prev) =>
                  prev.map(msg =>
                    msg.responseId === responseId
                      ? {
                        ...msg,
                        content: content,
                        deepResearchData: {
                          id: pollData.id,
                          model: selectedModel,
                          reasoning: pollData.reasoning?.summary || null,
                          citations: pollData.citations || [],
                          usage: pollData.usage || null
                        },
                        responseId: undefined,
                        isProcessing: false
                      }
                      : msg
                  )
                );

              } else if (pollData.status === 'failed') {
                setLoading(false);
                setChatHistory((prev) =>
                  prev.map(msg =>
                    msg.responseId === responseId
                      ? {
                        ...msg,
                        content: "Deep Research failed. The query may be too complex or there was a server error. Please try again with a simpler request.",
                        responseId: undefined,
                        isProcessing: false
                      }
                      : msg
                  )
                );

              } else {
                // Still processing (queued, in_progress), poll again
                setTimeout(pollForCompletion, 30000); // Poll every 30 seconds
              }

            } catch (error) {
              console.error('Deep Research polling error:', error);
              setLoading(false);
              setChatHistory((prev) =>
                prev.map(msg =>
                  msg.responseId === responseId
                    ? {
                      ...msg,
                      content: `Deep Research polling failed: ${error.message}. Please try again.`,
                      responseId: undefined,
                      isProcessing: false
                    }
                    : msg
                )
              );
            }
          };

          // Start polling after 30 seconds
          setTimeout(pollForCompletion, 30000);

          return; // Exit early since we're handling this asynchronously

        } catch (error) {
          console.error("Error starting Deep Research:", error);
          setChatHistory((prev) => [...prev, {
            role: "assistant",
            content: `Failed to start Deep Research: ${error.message}. Please try again.`
          }]);
          setLoading(false);
          return;
        }
      }
      // END OF WJ CHANGE

      else {
        // Generic-AI-handleprompt-streamed
        lambdaUrl =
          "https://qipgrgsrzr57wb3lvmpjjwuqy40wyxwl.lambda-url.ap-southeast-1.on.aws/";
        payload = {
          model: selectedModel,
          systemPrompt,
          enhancedUserPrompt,
        };
      }

      // Check if an image generation toggle is enabled
      if (enableImageGen) {
        let isRefined = false; // Check if image is refined 
        try {
          // console.log(`if(enableImageGen) block is run`);
          let finalPrompt = currentUserPrompt;

          if (selectedModel.startsWith("gemini-")) {
            console.log(`Using Gemini image generation for model: ${selectedModel}`);

            // For Gemini models, NO refinement needed - use prompt directly
            const imageFiles = fileList?.filter((f) => /\.(png|jpe?g|webp)$/i.test(f.name)) || [];

            // Use conversation history for context (important for "make it greener" type requests)
            const conversationForImage = chatHistory.slice(-10); // Last 5 exchanges

            const result = await generateGeminiImage(currentUserPrompt, conversationForImage, imageFiles, setChatHistory);

            // Update the loading message with the result
            setChatHistory((prev) => {
              const newHistory = [...prev];
              const lastMessage = newHistory[newHistory.length - 1];

              if (
                lastMessage &&
                lastMessage.role === "assistant" &&
                lastMessage.imageGenerating === true &&
                lastMessage.content === "..."
                
                // lastMessage && lastMessage.role === "assistant" &&
                // lastMessage.content[0] && lastMessage.content[0].text === "🎨 Generating image with Gemini..." &&
                // lastMessage.content[0].streaming === true
              ) {
                // Replace the loading message with the result
                lastMessage.content = [
                  { type: "text", text: result.responseText },
                  { type: "image_url", image_url: result.imageUrl, streaming: false }
                ];
              }
              return newHistory;
            });

            // Clear image files after successful generation
            clearImagesOnly();
            setImagePreviews([]);

            setLoading(false);
            return; // Exit early for Gemini image generation
          }

          if (!IMAGE_CAPABLE_MODELS.has(selectedModel)) {
            if (selectedModel.startsWith("o3-deep-research") || selectedModel.startsWith("o4-mini-deep-research")) {
              console.log(`Selected Model for prompt refinement: ${selectedModel}`);
              removeThinkingBubble();
              setIsRefiningPrompt(true);
              setChatHistory(prev => [...prev, { role: "assistant", content: "..." }]);

              const { lambdaUrl: refineUrl, payload: refinePayload } =
                buildRefineRequest(selectedModel, systemPrompt, currentUserPrompt, visualState, reasoningEffort, gptOssReasoning);

              // Fire initial request
              const response = await fetch(refineUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(refinePayload),
              });

              if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Refine request failed: ${response.status} - ${errorText}`);
              }

              const data = await response.json();
              const responseId = data.id;

              if (!responseId) throw new Error("No response ID received from Deep Research API");

              // Poll for refined output
              const pollForCompletion = async () => {
                try {
                  const pollResponse = await fetch(`${refineUrl}?action=poll&responseId=${responseId}`);
                  if (!pollResponse.ok) throw new Error(`Polling failed: ${pollResponse.status}`);

                  const pollData = await pollResponse.json();

                  if (pollData.status === "completed") {
                    if (!pollData.output?.length) throw new Error("No research output received");

                    const finalOutput = pollData.output[pollData.output.length - 1];
                    const content = finalOutput?.content?.[0]?.text;
                    if (!content) throw new Error("Invalid research output format");

                    finalPrompt = content.trim();
                    isRefined = true;

                    // Remove placeholder message
                    setChatHistory(prev => prev.slice(0, -1));
                    setIsRefiningPrompt(false);

                    // ✅ Now trigger image generation with GPT-5
                    await generateImage("gpt-5", finalPrompt, conversationSummary, isRefined);

                    setLoading(false);
                    return;
                  } else if (pollData.status === "failed") {
                    throw new Error("Deep Research failed. Try a simpler request.");
                  } else {
                    // Still queued or in_progress
                    setTimeout(pollForCompletion, 30000); // poll again
                  }
                } catch (error) {
                  console.error("Deep Research polling error:", error);
                  setChatHistory(prev => prev.slice(0, -1).concat({
                    role: "assistant",
                    content: `❌ Deep Research polling failed: ${error.message}`
                  }));
                  setIsRefiningPrompt(false);
                  setLoading(false);
                }
              };

              // Start polling
              setTimeout(pollForCompletion, 30000);
              return; // 🚪 Exit early because poll will handle the rest
            }

            // For non deep research models, do a single refine request
            removeThinkingBubble();
            setIsRefiningPrompt(true);
            setChatHistory(prev => [...prev, { role: "assistant", content: "..." }]);

            const { lambdaUrl: refineUrl, payload: refinePayload } =
              buildRefineRequest(selectedModel, systemPrompt, currentUserPrompt, visualState, reasoningEffort, gptOssReasoning);

            const refineResp = await fetch(refineUrl, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(refinePayload)
            });

            if (!refineResp.ok) {
              const errorText = await refineResp.text();
              throw new Error(`Refine request failed: ${refineResp.status} - ${errorText}`);
            }

            finalPrompt = await parseRefineResponse(selectedModel, refineResp) || currentUserPrompt;

            if (finalPrompt) {
              isRefined = true;
            }

            // console.log(`Selected Model for prompt refinement: ${selectedModel}`);
            // console.log(`Final Prompt: ${finalPrompt}`);


            setChatHistory(prev => prev.slice(0, -1));
            setIsRefiningPrompt(false);
          }

          const { imageUrl, errorCode } = await generateImage(selectedModel, finalPrompt, conversationSummary, isRefined);

          // console.log(`Generated image URL: ${imageUrl}`);
          // If nothing came back at all:
          if (!imageUrl) {
            const newCurrentUserPrompt =
              `Generate an image based on the following description: ${currentUserPrompt}`;

            const isModeration =
              errorCode && String(errorCode).toLowerCase().includes("moderation");

            // 🔹 Only show the generic message if it's NOT a moderation error
            if (!isModeration) {
              showAssistantError(
                "The image didn't load properly this time. Click on the button to try again with a better prompt.",
                newCurrentUserPrompt
              );
            }

            setLoading(false);
            return;
          }

          setLoading(false);
          isRefined = false; // reset
          return;
        } catch (err) {
          console.error("❌ Prompt refinement failed:", err);
          removeThinkingBubble();

          // Replace the "✍️ Refining prompt..." message with an error message
          setChatHistory(prev => {
            const newHistory = [...prev];
            if (
              newHistory.length &&
              newHistory[newHistory.length - 1].content === "..."
            ) {
              newHistory[newHistory.length - 1] = {
                role: "assistant",
                content: `❌ Prompt refinement failed: ${err.message}`
              };
            } else {
              newHistory.push({
                role: "assistant",
                content: `❌ Prompt refinement failed: ${err.message}`
              });
            }
            return newHistory;
          });

          setIsRefiningPrompt(false);
          throw err; // propagate so outer catch handles as well
        }
      }

      const response = await fetch(lambdaUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      // 🔹 Handle HTTP errors (e.g. 502 Bad Gateway) BEFORE doing any parsing/streaming
      if (!response.ok) {
        let errorText = "";
        try {
          errorText = await response.text();
        } catch {
          // ignore parse failure, we'll just show generic message
        }

        console.error(
          "Model server error:",
          response.status,
          response.statusText,
          errorText
        );

        let userMessage =
          "There was a problem contacting the model server. Please try again.";

        if (response.status === 502 || response.status === 504) {
          userMessage =
            "The model server is temporarily unavailable (Bad Gateway). Please try again in a moment.";
        } else if (response.status === 413) {
          userMessage =
            "Your request (including the uploaded files) is too large for this model. Try removing some files or using smaller documents.";
        }

        // Optional: look for specific messages coming back from your Lambda
        if (
          typeof errorText === "string" &&
          errorText.includes("total token size of the files")
        ) {
          userMessage =
            "Your uploaded documents contain too much text for a single request. Try asking about fewer files or smaller sections of the document.";
        }

        // Replace the "..." bubble with the friendly error
        showAssistantError(userMessage, currentUserPrompt);

        setLoading(false);
        return; // ⛔ stop here, don't try to parse/stream the body
      }


      // console.log("Payload 777:", JSON.stringify(payload, null, 2));

      if (
        selectedModel === "o1" ||
        selectedModel === "o3" ||
        // selectedModel === 'o3-mini' ||
        selectedModel === "azure-o1" ||
        selectedModel === "azure-o1-mini"
      ) {
        // Handle non-streamed response
        const data = await response.json();
        const relevant = ragData?.relevantChunks ?? [];
        const docIds = new Set(
          relevant.map(c => c.source || c.fileKey || c.docId).filter(Boolean)
        );

        const uploadedCount = Array.isArray(uploadedFiles) ? uploadedFiles.length : 0;


        const assistantMessage = {
          role: "assistant",
          content: data.response,
          ragMetadata: ragMetadata ? {
            selectedChunks: ragMetadata.selectedChunks,
            maxSimilarity: ragMetadata.maxSimilarity,
            sourceCount: docIds.size,     // still available if you want it
            uploadedCount
          } : null
        };
        removeThinkingBubble();
        setChatHistory((prev) => [...prev, assistantMessage]);
      } else if (selectedModel.startsWith("claude-")) {
        // Handle non-streamed response for specifically Claude models
        const data = await response.json();

        try {
          if (!data.response) {
            console.error("No response received from Claude model.");
            setChatHistory((prev) => [
              ...prev,
              {
                role: "assistant",
                content: "No response received from the model.",
              },
            ]);
            return;
          }
          const relevant = ragData?.relevantChunks ?? [];
          const docIds = new Set(
            relevant.map(c => c.source || c.fileKey || c.docId).filter(Boolean)
          );

          const uploadedCount = Array.isArray(uploadedFiles) ? uploadedFiles.length : 0;


          const assistantMessage = {
            role: "assistant",
            content: data.response,
            ragMetadata: ragMetadata ? {
              selectedChunks: ragMetadata.selectedChunks,
              maxSimilarity: ragMetadata.maxSimilarity,
              sourceCount: docIds.size,
              uploadedCount
            } : null
          };
          removeThinkingBubble();
          setChatHistory((prev) => [...prev, assistantMessage]);
        } catch (error) {
          console.error("Error processing response from Claude model:", error);
          setChatHistory((prev) => [
            ...prev,
            {
              role: "assistant",
              content: "Failed to process the model's response.",
            },
          ]);
        }
      }
      else if (selectedModel.startsWith("nova-")) {
        const data = await response.json();

        try {
          if (!data.response) {
            console.error("No response received from Nova model.");
            removeThinkingBubble();
            setChatHistory((prev) => [
              ...prev,
              {
                role: "assistant",
                content: "No response received from the model.",
              },
            ]);
            return;
          }
          const relevant = ragData?.relevantChunks ?? [];
          const docIds = new Set(
            relevant.map(c => c.source || c.fileKey || c.docId).filter(Boolean)
          );

          const uploadedCount = Array.isArray(uploadedFiles) ? uploadedFiles.length : 0;


          const assistantMessage = {
            role: "assistant",
            content: data.response,
            ragMetadata: ragMetadata ? {
              selectedChunks: ragMetadata.selectedChunks,
              maxSimilarity: ragMetadata.maxSimilarity,
              sourceCount: docIds.size,
              uploadedCount
            } : null
          };
          removeThinkingBubble();
          setChatHistory((prev) => [...prev, assistantMessage]);
        } catch (error) {
          console.error("Error processing response from Nova model:", error);
          setChatHistory((prev) => [
            ...prev,
            {
              role: "assistant",
              content: "Failed to process the model's response.",
            },
          ]);
        }
      }

      else if (selectedModel.startsWith("gemini-")) {
        const raw = await response.text();
        let data;
        try { data = JSON.parse(raw); } catch { data = null; }

        // unwrap { statusCode, body: "json" }
        if (data && typeof data === "object" && typeof data.body === "string" && !data.output) {
          try { data = JSON.parse(data.body); } catch {/* ignore */ }
        }

        let text =
          data?.output?.[0]?.content?.[0]?.text ??
          data?.response ??
          data?.text ?? "";

        text = typeof text === "string" ? text.trim() : "";

        if (!text) {
          const blockReason =
            data?.detail?.promptFeedback?.blockReason ||
            data?.promptFeedback?.blockReason;
          const msg = blockReason
            ? `Gemini returned no text (blocked: ${blockReason}).`
            : "No response received from Gemini.";
          removeThinkingBubble();
          // setChatHistory((prev) => [...prev, { role: "assistant", content: msg }]);
          showAssistantError(msg, currentUserPrompt);
        } else {
          removeThinkingBubble();
          setChatHistory((prev) => [...prev, { role: "assistant", content: text }]);
        }
      }

      // sea lion models
      else if (selectedModel.startsWith("sealion-")) {
        const data = await response.json();

        try {
          if (!data.response) {
            console.error("No responses received from SEA-LION model.");
            removeThinkingBubble();
            setChatHistory((prev) => [
              ...prev,
              {
                role: "assistant",
                content: "No response received from the SEA-LION model.",
              },
            ])
            return;
          }
          const assistantMessage = {
            role: "assistant",
            content: data.response,
          };
          removeThinkingBubble();
          setChatHistory((prev) => [...prev, assistantMessage]);
        } catch (error) {
          console.error("Error processing response from SEA-LION model:", error);
          setChatHistory((prev) => [
            ...prev,
            {
              role: "assistant",
              content: "Failed to process the SEA-LION model's response.",
            },
          ]);
        }
      }

      else if (selectedModel.startsWith("deepseek-")) {
        const data = await response.json();

        try {
          if (!data.response) {
            console.error("No response received from DeepSeek model.");
            removeThinkingBubble();
            setChatHistory((prev) => [
              ...prev,
              {
                role: "assistant",
                content: "No response received from the DeepSeek model.",
              },
            ]);
            return;
          }

          const assistantMessage = {
            role: "assistant",
            content: data.response,
            usage: data.usage || null,
            model_used: data.model_used || selectedModel
          };
          removeThinkingBubble();
          setChatHistory((prev) => [...prev, assistantMessage]);
        } catch (error) {
          console.error("Error processing response from DeepSeek model:", error);
          removeThinkingBubble();
          setChatHistory((prev) => [
            ...prev,
            {
              role: "assistant",
              content: "Failed to process the DeepSeek model's response.",
            },
          ]);
        }
      }

      else if (selectedModel.startsWith("sonar")) {
        const data = await response.json();

        try {
          if (!data.response && !data.choices) {
            console.error("No response received from Perplexity model.");
            removeThinkingBubble();
            setChatHistory((prev) => [
              ...prev,
              {
                role: "assistant",
                content: "No response received from the Perplexity model.",
              },
            ]);
            return;
          }

          const content = data.response || data.choices[0].message.content;
          const relevant = ragData?.relevantChunks ?? [];
          const docIds = new Set(
            relevant.map(c => c.source || c.fileKey || c.docId).filter(Boolean)
          );

          const uploadedCount = Array.isArray(uploadedFiles) ? uploadedFiles.length : 0;


          // Create assistant message with citations if available
          const assistantMessage = {
            role: "assistant",
            content: content,
            citations: data.citations || [],
            perplexityData: {
              id: data.id,
              model: data.model,
              usage: data.usage
            },
            ragMetadata: ragMetadata ? {
              selectedChunks: ragMetadata.selectedChunks,
              maxSimilarity: ragMetadata.maxSimilarity,
              sourceCount: docIds.size,
              uploadedCount
            } : null
          };
          removeThinkingBubble();
          setChatHistory((prev) => [...prev, assistantMessage]);
        } catch (error) {
          console.error("Error processing response from Perplexity model:", error);
          setChatHistory((prev) => [
            ...prev,
            {
              role: "assistant",
              content: "Failed to process the Perplexity model's response.",
            },
          ]);
        }

      } 
      else if (selectedModel.startsWith("gpt-oss-")) {
        const data = await response.json();

        try {
          if (!data.response) {
            console.error("No response received from GPT-OSS model.");
            setChatHistory((prev) => [
              ...prev,
              {
                role: "assistant",
                content: "No response received from the GPT-OSS model.",
              },
            ]);
            return;
          }

          const relevant = ragData?.relevantChunks ?? [];
          const docIds = new Set(
            relevant.map(c => c.source || c.fileKey || c.docId).filter(Boolean)
          );

          const uploadedCount = Array.isArray(uploadedFiles) ? uploadedFiles.length : 0;

          const assistantMessage = {
            role: "assistant",
            content: data.response,
            ragMetadata: ragMetadata ? {
              selectedChunks: ragMetadata.selectedChunks,
              maxSimilarity: ragMetadata.maxSimilarity,
              sourceCount: docIds.size,
              uploadedCount
            } : null
          };
          removeThinkingBubble();
          setChatHistory((prev) => [...prev, assistantMessage]);
        } catch (error) {
          console.error("Error processing response from GPT-OSS model:", error);
          removeThinkingBubble();
          setChatHistory((prev) => [
            ...prev,
            {
              role: "assistant",
              content: "Failed to process the GPT-OSS model's response.",
            },
          ]);
        }
      }
      else if (selectedModel.includes("codex") || selectedModel === "gpt-5.1") {
        const data = await response.json();

        try {
          if (!data.response) {
            console.error("No response received from GPT-Codex model.");
            setChatHistory((prev) => [
              ...prev,
              {
                role: "assistant",
                content: "No response received from the GPT-Codex model.",
              },
            ]);
            return;
          }

          const relevant = ragData?.relevantChunks ?? [];
          const docIds = new Set(
            relevant.map(c => c.source || c.fileKey || c.docId).filter(Boolean)
          );

          const uploadedCount = Array.isArray(uploadedFiles) ? uploadedFiles.length : 0;

          const assistantMessage = {
            role: "assistant",
            content: data.response,
            ragMetadata: ragMetadata ? {
              selectedChunks: ragMetadata.selectedChunks,
              maxSimilarity: ragMetadata.maxSimilarity,
              sourceCount: docIds.size,
              uploadedCount
            } : null
          };
          removeThinkingBubble();
          setChatHistory((prev) => [...prev, assistantMessage]);
        } catch (error) {
          console.error("Error processing response from GPT-Codex model:", error);
          removeThinkingBubble();
          setChatHistory((prev) => [
            ...prev,
            {
              role: "assistant",
              content: "Failed to process the GPT-Codex model's response.",
            },
          ]);
        }
      }

      // WJ CHANGE
      else if (selectedModel.startsWith("o3-deep-research") || selectedModel.startsWith("o4-mini-deep-research")) {
        // Handle Deep Research response (likely non-streamed)
        const data = await response.json();

        try {
          if (!data.output || !data.output.length) {
            console.error("No output received from Deep Research model.");
            removeThinkingBubble();
            setChatHistory((prev) => [
              ...prev,
              {
                role: "assistant",
                content: "No response received from the Deep Research model.",
              },
            ]);
            return;
          }

          if (!data || typeof data !== 'object') {
            throw new Error('Invalid response from Deep Research API');
          }

          if (!data.output || !Array.isArray(data.output) || data.output.length === 0) {
            throw new Error('No research output received from Deep Research API');
          }

          // Extract the final research report
          const finalOutput = data.output[data.output.length - 1];

          if (!finalOutput || !finalOutput.content || !Array.isArray(finalOutput.content) || finalOutput.content.length === 0) {
            throw new Error('Invalid research output format');
          }

          if (!finalOutput.content[0] || typeof finalOutput.content[0].text !== 'string') {
            throw new Error('No text content found in research output');
          }

          const content = finalOutput.content[0].text;

          // Calculate RAG metadata if available (same pattern as other models)
          const relevant = ragData?.relevantChunks ?? [];
          const docIds = new Set(
            relevant.map(c => c.source || c.fileKey || c.docId).filter(Boolean)
          );
          const uploadedCount = Array.isArray(uploadedFiles) ? uploadedFiles.length : 0;

          // Deep Research provides citations and reasoning
          const assistantMessage = {
            role: "assistant",
            content: content,
            deepResearchData: {
              id: data.id,
              model: selectedModel,
              reasoning: data.reasoning?.summary || null,
              citations: data.citations || [],
              usage: data.usage || null
            },
            ragMetadata: ragMetadata ? {
              selectedChunks: ragMetadata.selectedChunks,
              maxSimilarity: ragMetadata.maxSimilarity,
              sourceCount: docIds.size,
              uploadedCount
            } : null
          };

          removeThinkingBubble();
          setChatHistory((prev) => [...prev, assistantMessage]);

        } catch (error) {
          console.error("Error processing response from Deep Research model:", error);

          let userFriendlyMessage;
          if (error.message.includes('timeout')) {
            userFriendlyMessage = "Deep Research timed out. This query may be too complex. Try breaking it into smaller parts or use o4-mini-deep-research for faster results.";
          } else if (error.message.includes('rate limit')) {
            userFriendlyMessage = "Too many Deep Research requests. Please wait a few minutes before trying again.";
          } else if (error.message.includes('Invalid response')) {
            userFriendlyMessage = "Deep Research returned an unexpected response. Please try again in a few moments.";
          } else if (error.message.includes('No research output')) {
            userFriendlyMessage = "Deep Research couldn't generate a report for this query. Try rephrasing your request or making it more specific.";
          } else {
            userFriendlyMessage = `Deep Research failed: ${error.message}. Please try again or contact support if the problem persists.`;
          }

          removeThinkingBubble();

          setChatHistory((prev) => [
            ...prev,
            {
              role: "assistant",
              content: userFriendlyMessage,
            },
          ]);


          showAssistantError(userMessage, currentUserPrompt);

        }
      }
      // END OF WJ CHANGE
      else {
        // stream responses
        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        // 🔹 remove "..." if present (do this BEFORE adding the stream target)
        removeThinkingBubble();


        const processStream = async () => {
          let done = false;
          while (!done) {
            const { value, done: streamDone } = await reader.read();
            if (value) {
              const chunk = decoder.decode(value, { stream: true });
              assistantResponse += chunk;
              
              setChatHistory((prev) => [
                ...prev.slice(0, -1),
                { role: "assistant", content: assistantResponse },
              ]);
            }
            done = streamDone;
          }

          // After streaming is complete, add RAG metadata

          const relevant = ragData?.relevantChunks ?? [];
          const docIds = new Set(
            relevant.map(c => c.source || c.fileKey || c.docId).filter(Boolean)
          );

          const uploadedCount = Array.isArray(uploadedFiles) ? uploadedFiles.length : 0;

          if (ragMetadata) {
            setChatHistory((prev) => [
              ...prev.slice(0, -1),
              {
                role: "assistant",
                content: assistantResponse,
                ragMetadata: {
                  selectedChunks: ragMetadata.selectedChunks,
                  maxSimilarity: ragMetadata.maxSimilarity,
                  sourceCount: docIds.size,
                  uploadedCount
                }
              },
            ]);
          }

          setLoading(false);
        };

        setChatHistory((prev) => [...prev, { role: "assistant", content: "" }]);
        await processStream();
      }

      // // Reset file list and clear extracted text
      // if (!isS3UploadEnabled && uploadMode !== 'direct') {
      //   // setFileList([]);
      //   // setExtractedText("");
      //   clearImagesOnly();
      //   setImagePreviews([]);
      //   setMaskPreview(null);
      //   setMaskSavedMessage(false);

      // }

    } catch (error) {
      console.error("Error:", error);

      // Build a user-friendly message
      const raw = typeof error?.message === "string" ? error.message : "";
      let userMessage =
        "Something went wrong while processing your request. Please try again.";

      if (raw.includes("total token size of the files")) {
        userMessage =
          "Your uploaded documents contain too much text for a single request.\n\n" +
          "Please reload the page, then try again with fewer files or smaller sections of the document.";
      } else if (raw.toLowerCase().includes("context length")) {
        userMessage =
          "That question plus the attached documents is too long for this model.\n\n" +
          "Please reload the page, then shorten your prompt or remove some files before trying again.";
      }

      // Replace the "..." bubble with an error message
      showAssistantError(userMessage, currentUserPrompt);


      // Clear files on error (as you already do)
      if (!isS3UploadEnabled) {
        setFileList([]);
        setExtractedText("");
        setImagePreviews([]);
        setMaskPreview(null);
        setMaskSavedMessage(false);
      }

      setLoading(false);
      return;
    }

    setLoading(false);
  };

  // Clear the conversation history
  const handleClearConversation = () => {
    // console.log("Clearing conversation and resetting image response ID");
    // console.log("Previous imageResponseId was:", imageResponseId);
    setChatHistory([]);
    setImageResponseId(null);

    // Clear all file-related state regardless of mode
    const fileCount = uploadedFiles.length;
    setUploadedFiles([]); // Clear uploaded files for all modes
    setFileList([]);
    setExtractedText("");
    setImagePreviews([]);
    setMaskPreview(null);
    setMaskSavedMessage(false);

    // Show appropriate message based on mode
    if (fileCount > 0) {
      let modeText = isS3UploadEnabled ? "RAG" : (uploadMode === 'direct' ? "direct" : "context");
      setFileWarningMessage(`Cleared conversation and removed ${fileCount} ${modeText} files`);
      setTimeout(() => setFileWarningMessage(""), 3000);
    }

    // console.log("Reset imageResponseId to null");
  };
  // Toggle the popup for editing the system prompt
  const togglePopup = () => {
    setIsPopupOpen(!isPopupOpen);
  };

  // Dynamically update the number of rows in the text area
  // const handleInputChange = (e) => {
  //   setUserPrompt(e.target.value);

  //   // Dynamically calculate the number of lines
  //   const lineCount = e.target.value.split(/\r*\n/).length;
  //   setTextAreaRows(Math.min(lineCount, 5)); // Limit to a max of 5 rows
  // };

  // Handle Enter key press to send the prompt
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevent default Enter behavior
      handleSendPrompt(); // Send the prompt
    }
  };

  const handleModeSwitch = async (newMode, wasDirectUploadModel, isDirectUploadModel) => {
    const oldMode = uploadMode;

    // Track whether there's a change in mode or model capabilities
    const modeChanged = oldMode !== newMode;
    const directModelChanged = wasDirectUploadModel !== isDirectUploadModel;

    // If no relevant changes, do nothing
    if (!modeChanged && !directModelChanged) return;

    // Check if user has files that will be cleared
    const hasFiles = fileList.length > 0;
    const hasUploadedFiles = uploadedFiles.length > 0;
    const hasAnyFiles = hasFiles || hasUploadedFiles;

    // Case 1: Mode switch (e.g., context -> RAG)
    if (modeChanged && hasAnyFiles) {
      const fileCount = Math.max(fileList.length, uploadedFiles.length);
      const shouldSwitch = window.confirm(
        `Switching from ${oldMode.toUpperCase()} to ${newMode.toUpperCase()} mode will clear all ${fileCount} uploaded files. Continue?`
      );

      if (!shouldSwitch) {
        // User cancelled, don't change mode
        return;
      }

      // Clear all file-related state
      setFileList([]);
      setUploadedFiles([]);
      setExtractedText("");
      setImagePreviews([]);
      setMaskPreview(null);
      setMaskSavedMessage(false);

      // Update mode
      setUploadMode(newMode);

      // Update S3 upload setting based on new mode
      if (newMode === 'rag') {
        setIsS3UploadEnabled(true);
      } else {
        setIsS3UploadEnabled(false);
      }

      // Show confirmation message
      setFileWarningMessage(`✅ Switched to ${newMode.toUpperCase()} mode. Please re-upload your files.`);
      setTimeout(() => setFileWarningMessage(""), 3000);
    }
    // Case 2: Model capability change - losing direct upload capability
    else if (directModelChanged && !isDirectUploadModel && oldMode === 'context') {
      // Find files that were uploaded for direct mode
      const directUploadFiles = uploadedFiles.filter(file => file.mode === 'direct');

      if (directUploadFiles.length > 0) {
        // Get filenames of direct uploaded files
        const directFileNames = directUploadFiles.map(file => file.filename);

        // Create new lists without the direct upload files
        const updatedFileList = fileList.filter(file =>
          !directFileNames.includes(file.name)
        );

        const updatedUploadedFiles = uploadedFiles.filter(file =>
          file.mode !== 'direct'
        );

        // Update image previews - remove any that were from direct upload
        const updatedImagePreviews = imagePreviews.filter(preview =>
          !directFileNames.includes(preview.name)
        );

        // Update file lists
        setFileList(updatedFileList);
        setUploadedFiles(updatedUploadedFiles);
        setImagePreviews(updatedImagePreviews);

        // Regenerate extracted text if in context mode
        if (updatedFileList.length > 0) {
          // Clear extracted text since we can't reliably regenerate it without the file data
          setExtractedText("");
        } else {
          setExtractedText("");
        }

        // Show warning message
        const fileCount = directFileNames.length;
        const fileWord = fileCount === 1 ? 'file' : 'files';
        setFileWarningMessage(
          `Removed ${fileCount} ${fileWord} that required GPT-4o/4o-mini. The selected model doesn't support direct file uploads. Please re-upload files if needed.`
        );
        setTimeout(() => setFileWarningMessage(""), 5000);
      }

      // If mode change as well, update mode settings
      if (modeChanged) {
        setUploadMode(newMode);
        if (newMode === 'rag') {
          setIsS3UploadEnabled(true);
        } else {
          setIsS3UploadEnabled(false);
        }
      }
    }
    // Case 3: Mode switch but no files to clear
    else if (modeChanged) {
      setUploadMode(newMode);
      if (newMode === 'rag') {
        setIsS3UploadEnabled(true);
      } else {
        setIsS3UploadEnabled(false);
      }
    }
  };

  function resetTextArea() {
    const ta = taRef.current;
    if (!ta) return;

    // wipe any inline height set by autosize and snap to 1 row
    ta.style.height = "0px";            // force shrink
    ta.style.overflowY = "hidden";
    resizeToContent(ta, { minRows: 1, maxRows: 5 });
  }

  const handleModelChange = (newModel) => {
    const wasDirectUploadModel =
      selectedModel === "gpt-4o" || selectedModel === "gpt-4o-mini" ||
      selectedModel === "gpt-5"  || selectedModel === "gpt-5-mini" || 
      selectedModel === "gpt-5-nano";

    if (newModel === selectedModel) return;

    setSelectedModel(newModel);
    setModelBarOpen(false); // optional: close drawer on mobile

    // announce in chat
    postSystemNotice(`${newModel}`);

    const isDirectUploadModel =
      newModel === "gpt-4o" || newModel === "gpt-4o-mini" ||
      newModel === "gpt-5"  || newModel === "gpt-5-mini" || newModel === "gpt-5-nano" || selectedModel.includes('codex');

    if (wasDirectUploadModel && !isDirectUploadModel) {
      const directUploadFiles = uploadedFiles.filter(f => f.mode === 'direct');
      if (directUploadFiles.length > 0) {
        const directFileNames = directUploadFiles.map(f => f.filename);
        setFileList(prev => prev.filter(f => !directFileNames.includes(f.name)));
        setUploadedFiles(prev => prev.filter(f => f.mode !== 'direct'));
        setImagePreviews(prev => prev.filter(p => !directFileNames.includes(p.name)));
        setExtractedText("");

        const fileCount = directFileNames.length;
        setFileWarningMessage(`⚠️ Removed ${fileCount} ${fileCount === 1 ? 'file' : 'files'} that required GPT-4o/4o-mini. The selected model doesn't support direct file uploads.`);
        setTimeout(() => setFileWarningMessage(""), 5000);
      }
    }

    if (wasDirectUploadModel !== isDirectUploadModel) {
      handleModeSwitch(uploadMode, wasDirectUploadModel, isDirectUploadModel);
    }
  };


  const resizeToContent = (el) => {
    if (!el) return;
    // compute line height to derive max height in px
    const lh = parseFloat(getComputedStyle(el).lineHeight || "24");
    const maxH = lh * 5;

    el.style.height = "auto";                         // let it shrink/grow
    const newH = Math.min(el.scrollHeight, maxH);     // cap at 5 rows
    el.style.height = `${newH}px`;
    el.style.overflowY = el.scrollHeight > maxH ? "auto" : "hidden"; // only scroll after 5 rows
  };

  const handleInputChange = (e) => {
    setUserPrompt(e.target.value);
    resizeToContent(e.target);
  };

  useEffect(() => resizeToContent(taRef.current), []);



  const postSystemNotice = (name) => {
    setChatHistory(prev => [
      ...prev,
      {
        role: "system",
        content: { type: "model-change", model: name },
        excludeFromPrompt: true,
      }
    ]);
  };

  // ===== put these near your component top =====
  const MODEL_META = {
    "azure-o1":      { reasoning: true, visual: false, fast: false, webdev: false, research: false },
    "azure-o1-mini": { reasoning: false, visual: false, fast: true,  webdev: false, research: false },
    "azure-o3-mini": { reasoning: true, visual: false, fast: true,  webdev: false, research: false },
    "azure-gpt-4o": { reasoning: false, visual: false, fast: false,  webdev: false, research: false },
    "azure-gpt-4o-mini": { reasoning: false, visual: false, fast: true,  webdev: false, research: false },
    "azure-gpt-4": { reasoning: false, visual: false, fast: false,  webdev: false, research: false },
    "o1":            { reasoning: true, visual: true, fast: false, webdev: false, research: false },
    "o3":            { reasoning: true, visual: true,  fast: false, webdev: false, research: false },
    "o3-mini":       { reasoning: true, visual: false, fast: true,  webdev: false, research: false },
    "o3-deep-research":       { reasoning: true, visual: false, fast: false,  webdev: false, research: true },
    "o4-mini-deep-research":       { reasoning: true, visual: true, fast: true,  webdev: false, research: true },
    "gpt-5":         { reasoning: true, visual: true,  fast: false, webdev: true, research: false  },
    "gpt-5-mini":    { reasoning: true, visual: true, fast: true, webdev: true, research: false  },
    "gpt-5-nano":    { reasoning: true, visual: true, fast: true, webdev: true, research: false  },
    "gpt-4o":        { reasoning: false, visual: true,  fast: false, webdev: false, research: false },
    "gpt-4o-mini":   { reasoning: false, visual: true, fast: true,  webdev: false, research: false },
    "gpt-4":        { reasoning: false, visual: false, fast: false,  webdev: false, research: false },
    "gpt-4.1":        { reasoning: false, visual: true, fast: false,  webdev: false, research: false },
    "claude-4-sonnet":        { reasoning: false, visual: true, fast: false,  webdev: true, research: false },
    "claude-3.7-sonnet":        { reasoning: false, visual: true, fast: false,  webdev: true, research: false },
    "claude-3.5-haiku":        { reasoning: false, visual: true, fast: false,  webdev: true, research: false },
    "claude-3.5-sonnet":        { reasoning: false, visual: true, fast: false,  webdev: true, research: false },
    "claude-3.5-sonnet-v2":        { reasoning: false, visual: true, fast: false,  webdev: true, research: false },
    "nova-lite-v1":        { reasoning: false, visual: false, fast: false,  webdev: false, research: false },
    "gemini-2.5-pro":        { reasoning: false, visual: true, fast: false,  webdev: true, research: false },
    "gemini-2.5-flash":        { reasoning: false, visual: true, fast: true,  webdev: true, research: false },
    "gemini-2.5-flash-lite":        { reasoning: false, visual: false, fast: true,  webdev: false, research: false },
    "sealion-v3.5-70B-R":        { reasoning: true, visual: false, fast: false,  webdev: false, research: false },
    "sealion-v3-70B-IT":        { reasoning: false, visual: false, fast: false,  webdev: false, research: false },
    "sealion-v4-27B-IT":        { reasoning: false, visual: false, fast: false,  webdev: false, research: false },
    "deepseek-r1":        { reasoning: false, visual: false, fast: false,  webdev: true, research: false },
    "sonar":        { reasoning: false, visual: false, fast: false,  webdev: false, research: false },
    "sonar-pro":        { reasoning: false, visual: false, fast: false,  webdev: false, research: false },
    "sonar-reasoning":        { reasoning: true, visual: false, fast: false,  webdev: false, research: false },
    "sonar-reasoning-pro":        { reasoning: true, visual: false, fast: false,  webdev: false, research: false },
    "sonar-deep-research":        { reasoning: true, visual: false, fast: false,  webdev: false, research: true },
    "gpt-oss-20b":        { reasoning: false, visual: false, fast: true,  webdev: false, research: false },
    "gpt-oss-120b":        { reasoning: false, visual: false, fast: false,  webdev: false, research: false },
    "gpt-5.1-codex":        { reasoning: false, visual: false, fast: false,  webdev: true, research: false },
    "gpt-5.1-codex-mini":        { reasoning: false, visual: false, fast: true,  webdev: true, research: false },
    "gpt-5-codex":       { reasoning: false, visual: false, fast: false,  webdev: true, research: false },
  };

  const FILTER_CHIPS = ["All","Reasoning","Fast","Visual","WebDev", "Research"];


  const CHIP_TEST = {
    Reasoning: (m) => MODEL_META[m]?.reasoning,
    Visual:    (m) => MODEL_META[m]?.visual,
    Fast:      (m) => MODEL_META[m]?.fast,
    WebDev:   (m) => MODEL_META[m]?.webdev,
    Research:   (m) => MODEL_META[m]?.research,
  };


  function toggleChip(chip) {
    setSelectedFilters((prev) => {
      // Clicking "All" resets to only All
      if (chip === "All") return ["All"];

      // Remove "All" if a specific chip is selected
      const base = prev.includes("All") ? [] : prev.slice();

      // Toggle chip
      const idx = base.indexOf(chip);
      if (idx >= 0) base.splice(idx, 1);
      else base.push(chip);

      // If nothing selected, fall back to All
      return base.length ? base : ["All"];
    });
  }


  // Flatten your categories → [{ name, category }]
  const allModels = Object.entries(modelCategories).flatMap(([category, models]) =>
    models.map((name) => ({ name, category }))
  );

  const filteredModels = allModels.filter(({ name }) => {
    // No filters or "All" -> everything
    if (!selectedFilters.length || selectedFilters.includes("All")) return true;

    // AND: every selected chip must be true for the model
    return selectedFilters.every((chip) => {
      const test = CHIP_TEST[chip];
      return typeof test === "function" ? !!test(name) : true;
    });
  });

  // text match on name (case-insensitive)
  const nameMatched = useMemo(() => {
    const q = modelSearch.trim().toLowerCase();
    if (!q) return filteredModels; // no extra filter
    return filteredModels.filter(({ name }) => name.toLowerCase().includes(q));
  }, [filteredModels, modelSearch]);

  // keep your "selected at top" logic but base it on nameMatched
  const orderedModels = useMemo(() => {
    const arr = nameMatched.slice();
    const i = arr.findIndex(m => m.name === selectedModel);
    if (i > 0) {
      const [hit] = arr.splice(i, 1);
      arr.unshift(hit);
    }
    return arr;
  }, [nameMatched, selectedModel]);


  const handleRemoveComposerFile = (name) => {
    setFileList(prev => prev.filter(f => f.name !== name));

    // keep uploadedFiles in sync (if you track by filename)
    setUploadedFiles(prev => prev.filter(u => u.filename !== name));

    // stop any fake/progress state for this file
    setProcessingFiles(prev => prev.filter(n => n !== name));
    setFileProgress(prev => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  // right before the JSX
  const nonImageFiles = fileList.filter(file => {
    const name = file?.name || "";
    const type = file?.type || "";
    const isImageByType = type.startsWith("image/");
    const isImageByExt = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
    return !(isImageByType || isImageByExt);
  });



  return (
    <>
      {isMobile 

      // Mobile Layout
        ? 
        <div className="fixed inset-0 flex flex-col overflow-hidden text-neutral-100 font-inter">
          {/* Top bar */}
          <header className="shrink-0 sticky top-0 z-40 border-b border-neutral-800 backdrop-blur">
            <div className="flex items-center justify-between pr-4 py-3">
              {/* Left: Logo */}
              <div className="flex-1 flex items-center justify-start pr-1">
                <img
                  src="/NTU-LKCMedicine_Rev-Colour.png"
                  alt="LKCMedicine"
                  className="h-14 w-auto object-contain"
                  draggable={false}
                />
              </div>

              {/* Center: Current Model */}
              <div className="flex-1 flex flex-col items-center justify-center" data-tour="model-select">
                <div className="text-[11px] text-neutral-400">Current model</div>
                <div className="truncate text-sm font-semibold">{selectedModel || "—"}</div>
              </div>

              
              <div className="flex-1 flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => setShowSettings(prev => !prev)}
                  className={`rounded-lg px-3 py-2 shadow-sm transition-all duration-200 ease-out transform
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/60
                    ${
                      showSettings
                        ? "bg-blue-700 text-white scale-95"
                        : "bg-blue-600 text-white hover:bg-blue-500 hover:scale-105 active:scale-95"
                    }`}
                  aria-label="Open settings"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </div>

              {/* Popup Backdrop */}
              {showSettings && (
                <div
                  className="fixed inset-0 z-50 transition-opacity duration-300 ease-out animate-fadeIn"
                  onClick={() => setShowSettings(false)}
                  aria-hidden="true"
                />
              )}

              {/* Settings Popup */}
              {showSettings && (
                <div className="fixed ml-auto inset-x-4 top-20 z-50 max-w-[50vw] animate-slideDown">
                  <div className="rounded-2xl border border-neutral-700 bg-neutral-900 shadow-2xl">
                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-neutral-800 px-4 py-3">
                      <h2 className="text-lg font-semibold text-neutral-100">Settings</h2>
                      <button
                        onClick={() => setShowSettings(false)}
                        className="rounded-lg p-1 text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100 transition-colors"
                        aria-label="Close settings"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Content (only actions) */}
                    <div className="p-4">
                      <div className="grid gap-3">
                        <button
                          onClick={() => setIsNewsletterOpen(true)}
                          className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2.5 text-white text-sm font-medium shadow-sm transition hover:bg-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                        >
                          <Mail className="h-4 w-4" />
                          Subscribe
                        </button>

                        <button
                          onClick={handleLogoutRedirect}
                          className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-red-800 px-3 py-2.5 text-white text-sm font-semibold shadow-sm transition hover:bg-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600"
                        >
                          <LogOut className="h-4 w-4" />
                          Log Out
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </header>


          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className={`flex-1 min-h-0 overflow-y-auto [-webkit-overflow-scrolling:touch] touch-pan-y overscroll-contain px-4 md:px-6 py-4`}
          >
            <div className="chat-history space-y-4 text-xl mx-auto">
              {chatHistory.map((message, mIdx) => (
                <div key={mIdx} className={`chat-message ${message.role}`}>
                  {message.role === "system" ? (
                    <div className="w-full flex justify-center">
                      {typeof message.content === "string" ? (
                        <div className="rounded-full bg-neutral-800/70 px-3 py-1 text-sm text-neutral-300">
                          {message.content}
                        </div>
                      ) : message.content?.type === "model-change" ? (
                        <div className="rounded-full bg-neutral-800/70 px-3 py-1 text-sm text-neutral-300">
                          Model changed to{" "}
                          <span className="ml-1 inline-flex items-center rounded-full border border-purple-500/50 bg-purple-500/15 px-2 py-0.5 text-xs font-semibold text-purple-300">
                            {message.content.model}
                          </span>
                        </div>
                      ) : (
                        <div className="rounded-full bg-neutral-800/70 px-3 py-1 text-sm text-neutral-300">
                          {JSON.stringify(message.content)}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="message-content">
                      {message.role === "assistant" && message.content === "..." ? (
                        message.imageGenerating ? (
                          <GeneratingImageLoader isMobile={isMobile}/>
                        ) : (
                          <TypingDots />
                        )
                      ) : Array.isArray(message.content) ? (
                        (() => {
                          const itemsWithIdx = message.content.map((it, i) => ({ ...it, __i: i }));
                          const files = itemsWithIdx.filter((it) => it.type === "file");
                          const images = itemsWithIdx.filter((it) => it.type === "image_url");
                          const texts = itemsWithIdx.filter((it) => it.type === "text");
                          const isAssistant = message.role === "assistant";

                          return (
                            <div className="space-y-2">
                              {files.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                  {files.map((it) => (
                                    <div
                                      key={`file-${it.__i}`}
                                      className="inline-flex items-center gap-3 rounded-3xl bg-[#262626] border border-[#4a4a4a] px-3 py-2"
                                    >
                                      <div className="h-10 w-10 rounded-2xl bg-[#ff4b4b] flex items-center justify-center">
                                        {getFileIcon ? getFileIcon(it.name) : (
                                          <span className="text-white text-xs font-bold">PDF</span>
                                        )}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-neutral-50 truncate">
                                          {it.name}
                                        </div>
                                        <div className="text-[11px] text-neutral-300">
                                          {(it.name.split(".").pop() || "FILE").toUpperCase()}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {images.map((it) => (
                                <img
                                  key={`img-${it.__i}`}
                                  src={it.image_url}
                                  alt="Uploaded"
                                  className={
                                    "my-4 rounded cursor-zoom-in object-contain " +
                                    (isAssistant ? "w-full max-h-[480px]" : "w-auto max-h-40 md:max-h-56")
                                  }
                                  onClick={() => {
                                    const key = `${mIdx}:${it.__i}`;
                                    const gIdx = galleryIndexByKey.get(key);
                                    if (gIdx !== undefined) openChatLightboxAt(gIdx);
                                  }}
                                />
                              ))}

                              {texts.map((it) => (
                                <p key={`txt-${it.__i}`}>{it.text}</p>
                              ))}
                            </div>
                          );
                        })()
                      ) : message.role === "assistant" ? (
                        <AssistantMarkdown
                          text={
                            message.citations && message.citations.length
                              ? formatResponseWithCitations(message.content, message.citations)
                              : message.content
                          }
                          allowHtml={Boolean(message.citations && message.citations.length)}
                        />
                      ) : message.citations && message.citations.length > 0 ? (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: formatResponseWithCitations(message.content, message.citations),
                          }}
                        />
                      ) : (
                        <p>{message.content}</p>
                      )}

                      {message.citations?.length > 0 && (
                        <div className="citations">
                          <details>
                            <summary>Sources ({message.citations.length})</summary>
                            <ul>
                              {message.citations.map((citation, i) => (
                                <li key={i}>
                                  <a href={citation} target="_blank" rel="noopener noreferrer">
                                    {new URL(citation).hostname}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </details>
                        </div>
                      )}

                      {message.deepResearchData?.citations?.length > 0 && (
                        <div className="deep-research-citations">
                          <details>
                            <summary>
                              Deep Research Sources ({message.deepResearchData.citations.length})
                            </summary>
                            <ul>
                              {message.deepResearchData.citations.map((c, i) => (
                                <li key={i}>
                                  <a href={c.url} target="_blank" rel="noopener noreferrer">
                                    {c.title || new URL(c.url).hostname}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </details>
                        </div>
                      )}

                      {message?.ragMetadata && (
                        <div className="rag-metadata">
                          <small>
                            Referenced {message.ragMetadata.uploadedCount ?? 0}{" "}
                            {(message.ragMetadata.uploadedCount ?? 0) === 1 ? "source" : "sources"} •
                            Similarity: {(((message.ragMetadata.maxSimilarity ?? 0) * 100).toFixed(1))}%
                          </small>
                        </div>
                      )}
                    </div>
                  )}

                  {message.role === "assistant" && message.error && message.regeneratePrompt && (
                    <div className="my-auto mx-3 flex items-center justify-end">
                      <button
                        onClick={() => handleRegenerate(message.regeneratePrompt)}
                        disabled={loading}
                        className={`
                          group relative inline-flex items-center gap-2
                          rounded-full border-2
                          px-4 py-2
                          text-sm font-medium
                          transition-all duration-200 ease-out
                          ${
                            loading
                              ? "border-neutral-700 bg-neutral-900 text-neutral-500 cursor-not-allowed"
                              : "border-neutral-600 bg-neutral-900 text-neutral-200 hover:border-neutral-400 hover:bg-neutral-800 active:scale-95"
                          }
                        `}
                      >
                        <svg
                          className={`w-5 h-5 transition-transform duration-200 ${
                            loading ? "opacity-50" : "group-hover:rotate-180"
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Bottom area (system + composer + dock) */}
          <div className="shrink-0 border-neutral-800 backdrop-blur">
            {/* System Prompt */}
            {selectedModel !== "o1-preview" &&
              selectedModel !== "o1-mini" &&
              selectedModel !== "azure-o1-mini" && (
                // ✅ CHANGE #1: wrapper that animates open/close (keep mounted)
                <div
                  className={[
                    "px-4 pb-2 transform-gpu transition-all duration-300 ease-out",
                    isPromptVisible
                      ? "opacity-100 translate-y-0 max-h-[520px]"
                      : "opacity-0 -translate-y-2 max-h-0 overflow-hidden pointer-events-none",
                  ].join(" ")}
                >
                  <div className="w-[100%] mx-auto rounded-2xl border border-neutral-700 bg-neutral-900 shadow px-3 py-1.5 md:py-2" data-tour="system-prompt">
                    <div
                      className="flex items-center justify-between gap-2 cursor-pointer"
                      onClick={togglePromptVisibility}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[12px] font-semibold tracking-wide text-neutral-200 uppercase">
                          System Prompt
                        </span>

                        <div className="relative group">
                          <button
                            data-tooltip-id="help-tip"
                            data-tooltip-place="top"
                            data-tooltip-delay-show={200}
                            aria-label="What does this do?"
                            className="h-5 w-5 grid place-items-center text-neutral-300 hover:text-white"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Info size={18} />
                          </button>

                          <Tooltip
                            id="help-tip"
                            clickable={false}
                            place="top"
                            effect="solid"
                            delayShow={200}
                            style={{
                              backgroundColor: "#2d3748",
                              color: "white",
                              padding: "6px 12px",
                              borderRadius: "10px",
                              fontSize: "0.875rem",
                              boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                              maxWidth: "220px",
                              textAlign: "center",
                              zIndex: 9999,
                            }}
                          >
                            Set rules for how the assistant should behave for this chat.
                          </Tooltip>
                        </div>

                        {dirty && (
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-red-500/40 bg-red-500/10 px-2 py-1 text-[11px] text-red-300">
                            <TriangleAlert className="h-3.5 w-3.5 text-red-400" aria-hidden="true" />
                            <span>Unsaved</span>
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          className="p-1 rounded hover:bg-neutral-800 text-neutral-300 hover:text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePromptVisibility();
                          }}
                          aria-label="Collapse/Expand"
                        >
                          {isPromptVisible ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                        </button>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApplySystemPrompt();
                          }}
                          className={`ml-1 h-10 w-10 grid place-items-center rounded-full transition
                            ${
                              applied && !dirty
                                ? "bg-emerald-500 text-white border hover:border-white border-emerald-500 shadow-md"
                                : "bg-neutral-800 text-neutral-200 hover:bg-neutral-700"
                            }`}
                          aria-label={applied && !dirty ? "Saved" : dirty ? "Save changes" : "Save"}
                          title={applied && !dirty ? "Saved" : dirty ? "Save changes" : "Save"}
                        >
                          {applied && !dirty ? <Check size={16} /> : <Save size={16} />}
                        </button>
                      </div>
                    </div>

                    
                    <div
                      className={[
                        "mt-1 rounded-xl border border-neutral-800 bg-neutral-900/60",
                        "transition-[max-height,opacity] duration-200",
                        isPromptVisible
                          ? "p-2 opacity-100 max-h-[400px]"
                          : "p-0 opacity-0 max-h-0 overflow-hidden pointer-events-none",
                      ].join(" ")}
                      aria-hidden={!isPromptVisible}
                    >
                      <div
                        ref={editorRef}
                        role="textbox"
                        aria-multiline="true"
                        contentEditable
                        suppressContentEditableWarning
                        onInput={handleEditableInput}
                        className="min-h-[40px] max-h-32 overflow-auto outline-none
                                  text-[13px] leading-5 text-neutral-100 whitespace-pre-wrap
                                  placeholder:text-neutral-500"
                        data-placeholder={`Set a constraint eg: "You are a helpful assistant that explains complex topics in simple terms."`}
                      />
                      <div className="mt-2 flex flex-wrap gap-2">
                        {[
                          "Act as…",
                          "Context:",
                          "Tone:",
                          "Format:",
                          // "Constraints:",
                          // "Explain step-by-step",
                          // "Examples:",
                        ].map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => insertChip(t)}
                            className="rounded-full border border-neutral-700 bg-neutral-800
                                      px-2 py-1 text-[12px] text-neutral-200 hover:bg-neutral-700"
                          >
                            {t}
                          </button>
                        ))}
                      </div>

                      {dirty && (
                        <p className="mt-2 text-[11px] text-neutral-400">
                          You have unsaved changes. Click{" "}
                          <span className="text-emerald-400">Save</span> (arrow) to apply.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

            {/* Composer */}
            <div className="px-4 py-3">
              <div className="rounded-2xl border border-neutral-700 bg-neutral-900 p-2 mx-auto w-[100%]">
                {/* Top row: input */}
                <div className="px-2 pt-2">

                  {/* Previews sit INSIDE the same border, above the textarea */}
                  {imagePreviews.length > 0 && (
                    <div className="flex gap-3 overflow-x-auto py-2">
                      {imagePreviews.map((preview, i) => (
                        <div
                          key={i}
                          className="relative h-24 w-24 shrink-0 rounded-2xl overflow-hidden border border-neutral-700 bg-neutral-800"
                        >
                          
                        <img
                          key={preview.name ?? i}
                          src={preview.url}
                          alt=""
                          className="h-24 w-24 object-cover rounded-2xl cursor-zoom-in"
                          onClick={() => openComposerLightboxAt(i)}
                        />


                          <button
                            onClick={() => removeFile(preview.name)}
                            className="absolute right-1 top-1 h-6 w-6 rounded-full bg-black/60 text-white text-xs hover:bg-red-600"
                            aria-label="Remove Preview"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* FILE TAB(S) LIKE CHATGPT */}
                  {nonImageFiles.length > 0 && (
                    <div className="mt-2">
                      <div className="flex flex-wrap gap-2">
                        {nonImageFiles.map((file) => {
                          const uploadedFile = uploadedFiles.find(u => u.filename === file.name);
                          const embeddingStatus = uploadedFile?.embeddingStatus;
                          const directStatus = uploadedFile?.status;
                          const isProcessing = processingFiles.includes(file.name);

                          const hasCompleteStatus =
                            embeddingStatus === "completed" ||
                            embeddingStatus === "failed" ||
                            directStatus === "uploaded" ||
                            directStatus === "failed";

                          const ext = (file.name.split(".").pop() || "").toUpperCase() || "FILE";
                          const showSpinner = isProcessing && !hasCompleteStatus;

                          return (
                            <div
                              key={file.name}
                              className="flex items-center gap-2 rounded-2xl bg-[#2b2b2b] border border-[#555] px-3 py-1.5"
                            >
                              {/* red square */}
                              <div className="h-7 w-7 rounded-xl bg-[#2b2b2b] flex items-center justify-center">
                                {showSpinner ? (
                                  <div className="h-4 w-4 border-2 border-white/35 border-t-white rounded-full animate-spin" />
                                ) : (
                                  getFileIcon(file.name)
                                )}
                              </div>

                              {/* filename + type */}
                              <div className="flex flex-col text-xs leading-tight">
                                <span className="font-semibold text-white break-all">
                                  {file.name}
                                </span>
                                <span className="text-[9px] text-gray-300">{ext}</span>
                              </div>

                              {/* close */}
                              <button
                                onClick={() => handleRemoveComposerFile(file.name)}
                                disabled={isProcessingUpload}
                                className={`ml-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/80
                                            text-white text-[9px] hover:bg-black ${
                                              isProcessingUpload ? "cursor-not-allowed" : ""
                                            }`}
                                aria-label={`Remove ${file.name}`}
                                title={`Remove ${file.name}`}
                              >
                                <X size={10} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <textarea
                    ref={taRef}
                    data-tour="prompt-input"
                    value={userPrompt}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask anything…"
                    rows={1}
                    className="w-full max-h-32 resize-none bg-transparent py-2 text-base md:text-sm text-neutral-100 outline-none"
                  />
                </div>

                {/* Bottom row: buttons */}
                <div className="mt-2 flex items-center justify-between gap-2 px-2 pb-1">
                  <div className="flex items-center gap-2">
                    
                    {/* MORE menu */}
                    <div
                      ref={moreMenuRef}
                      title="More tools"
                      className="relative flex items-center gap-2"
                    >
                      <button
                        type="button"
                        onClick={() => setShowMoreOptions(prev => !prev)}
                        aria-label="More tools"
                        data-tour="more-tools"
                        className={`icon-btn h-10 w-10 flex items-center justify-center rounded-full border-2 bg-neutral-800 text-neutral-200 transition-all duration-200 ease-out transform
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60
                        ${!loading ? "hover:scale-105 active:scale-95" : ""}
                        ${
                          showMoreOptions
                            ? "border-violet-400 text-violet-300 bg-violet-950/50"
                            : "border-neutral-600 hover:border-white hover:text-white"
                        }`}
                      >
                        <Grid2x2Plus size={25} />
                      </button>

                      <div
                        className={`absolute z-20 bottom-full mb-3 min-w-[240px]
                          rounded-xl border-2 border-neutral-700/80 bg-neutral-900/95 backdrop-blur-sm shadow-2xl
                          p-2 flex flex-col gap-1.5
                          transform origin-bottom
                          transition-all duration-200 ease-out
                          ${
                            showMoreOptions
                              ? "opacity-100 scale-100 translate-x-[-17px] pointer-events-auto"
                              : "opacity-0 scale-95 translate-y-2 pointer-events-none"
                          }
                        `}
                      >
                        {[
                          {
                            type: "react", // use Lucide icon
                            icon: Plus,
                            text: "Upload document",
                            action: toggleFilePopup,
                            tour: "upload",
                          },
                          {
                            type: "svg",    // use SVG from /public
                            src: "/speech_to_text.svg",
                            text: "Audio transcription",
                            action: () => setIsAudioPopupOpen(true),
                            tour: "transcription",
                          },
                          {
                            type: "svg",    // use SVG from /public
                            src: "/PDF_to_PPT.svg",
                            text: "Summarise PDF to PPTX",
                            action: () => setIsPDFUploadOpen(true),
                            tour: "ppt",
                          },
                        ].map((item, idx) => (
                          <button
                            key={idx}
                            data-tour={item.tour}
                            onClick={item.action}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-neutral-200 hover:bg-neutral-800/80 hover:text-violet-200 transition-all duration-150 group"
                            style={{
                              animation: showMoreOptions
                                ? `slideIn 0.25s ease-out ${idx * 0.05}s both`
                                : "none",
                            }}
                          >
                            {item.type === "react" ? (
                              <item.icon
                                size={20}
                                className="group-hover:scale-110 transition-transform duration-150"
                              />
                            ) : (
                              <img
                                src={item.src}
                                alt=""
                                className="h-5 w-5 invert group-hover:scale-110 transition-transform duration-150"
                              />
                            )}

                            <span className="font-medium">{item.text}</span>
                          </button>
                        ))}
                      </div>
                    </div>


                    {/* Icon toggle button */}
                    <button
                      type="button"
                      title={enableImageGen ? "Disable image generation" : "Enable image generation"}
                      onClick={() => setEnableImageGen(prev => !prev)}
                      aria-pressed={enableImageGen}
                      aria-label={enableImageGen ? "Disable image generation" : "Enable image generation"}
                      className={`
                        relative overflow-hidden
                        flex items-center justify-center
                        h-10 w-10 rounded-full border-2 text-base
                        transition-all duration-200 ease-out transform
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60
                        ${!loading ? "hover:scale-105 active:scale-95" : ""}
                        ${
                          enableImageGen
                            ? "border-violet-300 text-white shadow-lg shadow-violet-500/40"
                            : "bg-neutral-800 border-neutral-600 text-neutral-300 hover:border-neutral-300"
                        }
                      `}
                    >
                      {/* Sliding background effect */}
                      <span
                        className={`
                          absolute inset-0 bg-violet-500 rounded-full
                          transition-transform duration-300 ease-out
                          ${enableImageGen ? "translate-x-0 translate-y-0" : "translate-x-[-150%] translate-y-[150%]"}
                        `}
                        style={{
                          transformOrigin: 'bottom right'
                        }}
                      />
                      
                      {/* Icon - positioned above the sliding background */}
                      <span className="relative z-10">
                        {enableImageGen ? (
                          <Image size={20} />
                        ) : (
                          <ImageOff size={20} />
                        )}
                      </span>
                    </button>
                  </div>

                  {/* left utilities */}
                  <div className="flex items-center gap-2">  
                    {enableImageGen && imagePreviews.length === 1 && (
                      <button onClick={() => handleEditImage(imagePreviews[0])} 
                      className={`inpainting-button icon-btn border-2
                        text-neutral-200 transition-all duration-200 ease-out transform
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60
                        ${!loading ? "hover:scale-105 active:scale-95" : ""}
                        ${loading ? "opacity-60 cursor-not-allowed" : ""}
                        bg-neutral-900 border-neutral-300 text-white hover:border-neutral-300
                      `}

                      aria-label="Inpainting">
                        Mask
                      </button>
                    )}
                            
                    {/* toggle on the far left */}
                    <button
                      onClick={handleClearConversation}
                      aria-label="Clear Conversation"
                      title="Clear Conversation"
                      disabled={loading}
                      className={`clear-button icon-btn bg-[#f44336] border-2 text-neutral-200 transition-all duration-200 ease-out transform
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60 grid h-10 w-10 place-items-center rounded-full
                        ${!loading ? "hover:scale-105 active:scale-95" : ""}
                        border-[#e53935] text-neutral-300 hover:border-neutral-300
                        `}
                    >
                      {/* brush icon */}
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-brush-cleaning-icon lucide-brush-cleaning">
                        <path d="m16 22-1-4"/>
                        <path d="M19 13.99a1 1 0 0 0 1-1V12a2 2 0 0 0-2-2h-3a1 1 0 0 1-1-1V4a2 2 0 0 0-4 0v5a1 1 0 0 1-1 1H6a2 2 0 0 0-2 2v.99a1 1 0 0 0 1 1"/>
                        <path d="M5 14h14l1.973 6.767A1 1 0 0 1 20 22H4a1 1 0 0 1-.973-1.233z"/>
                        <path d="m8 22 1-4"/>
                      </svg>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleSendPrompt()}
                      disabled={loading}
                      className="grid h-10 w-10 place-items-center rounded-full bg-green-600 text-white hover:bg-blue-500 disabled:opacity-60"
                      aria-label="Send"
                    >
                      <Send className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex justify-around text-[12px] text-neutral-400">
                <button
                  type="button"
                  onClick={() => setModelBarOpen(true)}
                  className="flex flex-col items-center gap-1"
                  data-tour="mobile-models-btn"
                >
                  <Grid2x2 className="h-5 w-5" />
                  <span>Models</span>
                </button>
                <button 
                  type="button"
                  onClick={togglePromptVisibility}
                  className="flex flex-col items-center gap-1"
                  data-tour="mobile-sysprompt-btn"
                >
                  <Sliders className="h-5 w-5" />
                  <span>System Prompt</span>
                </button>
                <button
                  type="button"
                  onClick={() => setisInfoOpen(true)}
                  className="flex flex-col items-center gap-1"
                >
                  <HelpCircle className="h-5 w-5" />
                  <span>Info</span>
                </button>
              </div>
            </div>
          </div>


          {/* Drawer + Backdrop */}
          {modelBarMounted && (
            <>
              <div
                className={[
                  "fixed inset-0 z-40 bg-black/70 backdrop-blur-sm transition-opacity duration-300",
                  modelBarOpen ? "opacity-100" : "opacity-0 pointer-events-none",
                ].join(" ")}
                onClick={closeModelBar}
                aria-hidden="true"
              />

              <aside
                data-sidebar
                style={{ "--sheetY": `${dragY}px` }}
                className={[
                  // mobile = bottom sheet, desktop = your existing layout
                  "fixed left-0 right-0 bottom-0 z-50 md:z-auto md:static",
                  "h-[75dvh] md:h-auto overflow-hidden flex flex-col",
                  "bg-neutral-900 text-neutral-100 border border-neutral-700 rounded-t-2xl md:rounded-2xl",

                  // padding (keep yours)
                  "p-4 md:p-4 pb-[calc(env(safe-area-inset-bottom)+24px)] md:pt-4",

                  // animation (mobile)
                  "transform transition-transform duration-300 ease-out md:transform-none",
                  modelBarOpen ? "translate-y-[var(--sheetY)]" : "translate-y-full md:translate-y-0",

                  "[scrollbar-gutter:stable] [scrollbar-width:thin] [scrollbar-color:theme(colors.violet.500)_transparent]",
                ].join(" ")}
              >
                

                <div className="flex h-full flex-col min-h-0 gap-4">
                  {/* Grabber (drag down to close) */}
                  <div
                    className="touch-none select-none"
                    onPointerDown={onGrabPointerDown}
                    onPointerMove={onGrabPointerMove}
                    onPointerUp={onGrabPointerEnd}
                    onPointerCancel={onGrabPointerEnd}
                  >
                    <div className="h-1.5 w-10 rounded-full bg-neutral-700/80 mx-auto" />
                  
                    <div className="space-y-3 mb-5">
                      

                      {/* SEARCH FILTER */}
                      <div className="relative pt-5">
                        <input
                          type="text"
                          data-tour="model-search"
                          value={modelSearch}
                          onChange={(e) => setModelSearch(e.target.value)}
                          placeholder="Search for models…"
                          className="w-full rounded-lg border border-neutral-700 bg-neutral-900 text-neutral-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/60 placeholder:text-neutral-500"
                          aria-label="Search models"
                        />

                        {/* Clear button */}
                        {modelSearch && (
                          <button
                            type="button"
                            onClick={() => setModelSearch("")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-200"
                            aria-label="Clear search"
                            title="Clear"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    

                    <div className="flex flex-nowrap gap-2 overflow-x-auto pb-4">
                      {FILTER_CHIPS.map((chip) => {
                        const active = selectedFilters.includes(chip);
                        return (
                          <button
                            key={chip}
                            type="button"
                            onClick={() => toggleChip(chip)}
                            aria-pressed={active}
                            className={`rounded-full border px-3 py-1.5 text-xs transition ${
                              active
                                ? "bg-violet-500 text-white border-violet-500"
                                : "bg-neutral-800 text-neutral-200 border-violet-700 hover:bg-neutral-700"
                            } focus:outline-none focus:ring-2 focus:ring-violet-500/60`}
                          >
                            {chip}
                          </button>
                        );
                      })}
                    </div>


                    {/* Reasoning Effort */}
                    {(selectedModel === "o3-mini" ||
                      selectedModel === "azure-o3-mini" ||
                      selectedModel === "o1" ||
                      selectedModel === "o3" ||
                      selectedModel === "azure-o1") && (
                      <div className="space-y-1">
                        <label htmlFor="reasoning-effort" className="text-xs text-neutral-300">
                          Reasoning Effort
                        </label>
                        <select
                          id="reasoning-effort"
                          value={reasoningEffort}
                          onChange={(e) => setReasoningEffort(e.target.value)}
                          disabled={loading}
                          className="w-full rounded-lg border border-neutral-700 bg-neutral-900 text-neutral-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/60"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                        <p className="text-xs text-neutral-400">
                          <span className="font-medium text-neutral-300">
                            {String(reasoningEffort).toUpperCase()}:
                          </span>{" "}
                          {reasoningEffort === "low" &&
                            "Optimized for speed while maintaining solid reasoning."}
                          {reasoningEffort === "medium" && "Balanced approach for most tasks."}
                          {reasoningEffort === "high" && "Enhanced reasoning for complex problems."}
                        </p>
                      </div>
                    )}

                    {/* GPT-OSS Reasoning */}
                    {selectedModel && selectedModel.startsWith("gpt-oss-") && (
                      <div className="space-y-1">
                        <label className="text-xs text-neutral-300">GPT-OSS Reasoning</label>
                        <select
                          value={gptOssReasoning}
                          onChange={(e) => setGptOssReasoning(e.target.value)}
                          disabled={loading}
                          className="w-full rounded-lg border border-neutral-700 bg-neutral-900 text-neutral-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/60"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                    )}

                    {/* AI MODELS header */}
                    {/* <div className="pt-2">
                      <h3 className="text-md font-semibold tracking-wide text-neutral-400">Models</h3>
                    </div> */}

                  </div>

                  <div className="px-2 -mt-2 flex-1 min-h-0 overflow-y-auto overscroll-contain pb-6">
                    {/* Sticky selected model */}
                    {selectedModel && (
                      <div className="sticky top-0 z-10 bg-neutral-900/90 backdrop-blur border-b border-neutral-800 pb-2">
                        <button
                          type="button"
                          onClick={() => handleModelChange(selectedModel)}
                          disabled={loading}
                          className="w-full text-left rounded-lg border border-purple-500 bg-purple-500/10 text-purple-300 shadow-sm px-3 py-2 text-sm"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{selectedModel}</span>
                            <span className="ml-2 rounded-full bg-purple-500 px-2 py-0.5 text-[10px] font-semibold text-white">
                              Selected
                            </span>
                          </div>
                        </button>
                      </div>
                    )}

                    {/* FILTERED MODEL LIST */}
                    <ul className="space-y-2">
                      {orderedModels
                        .filter(({ name }) => name !== selectedModel)
                        .map(({ name, category }) => {
                          const active = selectedModel === name;
                          return (
                            <li key={`${category}-${name}`}>
                              <button
                                type="button"
                                onClick={() => handleModelChange(name)}
                                disabled={loading}
                                className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition ${
                                  active
                                    ? "border-purple-500 bg-purple-500/10 text-purple-300 shadow-sm"
                                    : "border-neutral-700 hover:bg-neutral-800 text-neutral-100"
                                } focus:outline-none focus:ring-2 focus:ring-purple-500/60`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium">{name}</span>
                                </div>
                              </button>
                            </li>
                          );
                        })}

                      {!nameMatched.length && (
                        <li className="px-1 text-xs text-neutral-400">
                          No models match this filter.
                        </li>
                      )}
                    </ul>
                  </div>

                  
                </div>
              </aside>
            </>
          )}


          {lightboxOpen && currentArray.length > 0 && (
            <div
              className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={closeLightbox}
              role="dialog"
              aria-modal="true"
            >
              {/* Close (top-right) */}
              <button
                onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
                className="fixed right-3 top-3 h-10 w-10 rounded-full bg-black/70 text-white grid place-items-center"
                aria-label="Close"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>

              {/* Image wrapper (clicks here won't close) */}
              <div
                className="relative max-w-[95vw] max-h-[95vh]"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={(lightboxSource === "composer" ? imagePreviews : gallery)[lightboxIndex].url}
                  alt=""
                  className="max-w-[95vw] max-h-[95vh] object-contain rounded-lg shadow-2xl"
                  draggable={false}
                />
              </div>

              {/* Fixed-position nav controls (independent of image size) */}
              {total > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); goPrev(); }}
                    className="fixed left-12 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-black/70 text-white grid place-items-center"
                    aria-label="Previous"
                    title="Previous"
                  >
                    <ChevronLeft className="h-7 w-7" />
                  </button>

                  <button
                    onClick={(e) => { e.stopPropagation(); goNext(); }}
                    className="fixed right-12 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-black/70 text-white grid place-items-center"
                    aria-label="Next"
                    title="Next"
                  >
                    <ChevronRight className="h-7 w-7" />
                  </button>

                  <span className="fixed bottom-3 left-1/2 -translate-x-1/2 text-xs text-white/80">
                    {lightboxIndex + 1} / {total}
                  </span>
                </>
              )}
            </div>
          )}

          {/* Popups */}
          <InfoPopup isOpen={isInfoOpen} onClose={() => setisInfoOpen(false)} />
          {isNewsletterOpen && (
            <NewsletterPopup isOpen={isNewsletterOpen} onClose={() => setIsNewsletterOpen(false)} />
          )}
          {!isNewsletterOpen && isUpdatesOpen && (
            <UpdatesPopup isOpen={isUpdatesOpen} onClose={() => setIsUpdatesOpen(false)} />
          )}
          {isEditPopupOpen && (
            <ImageEditor
              imageToEdit={imageToEdit}
              setIsEditPopupOpen={setIsEditPopupOpen}
              setMaskPreview={setMaskPreview}
              maskPreview={maskPreview}
              maskSavedMessage={maskSavedMessage}
              setMaskSavedMessage={setMaskSavedMessage}
            />
          )}
          <FileUpload
            isOpen={isFilePopupOpen}
            onClose={toggleFilePopup}
            uploadMode={uploadMode}
            setUploadMode={handleModeSwitch}
            fileList={fileList}
            setFileList={setFileList}
            fileWarningMessage={fileWarningMessage}
            setFileWarningMessage={setFileWarningMessage}
            setIsS3UploadEnabled={setIsS3UploadEnabled}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            extractedText={extractedText}
            setExtractedText={setExtractedText}
            imagePreviews={imagePreviews}
            setImagePreviews={setImagePreviews}
            isDirectUploadModelSelected={
              selectedModel === "gpt-4o" ||
              selectedModel === "gpt-4o-mini" ||
              selectedModel === "gpt-5" ||
              selectedModel === "gpt-5-mini" ||
              selectedModel === "gpt-5-nano"
            }
          />
          {isPopupOpen && (
            <div className="popup">
              <div className="popup-content">
                <h2>Edit System Prompt</h2>
                <textarea
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  rows="12"
                  cols="25"
                />
                <button onClick={togglePopup}>Confirm</button>
              </div>
            </div>
          )}
          <AudioTranscription isOpen={isAudioPopupOpen} onClose={() => setIsAudioPopupOpen(false)} />
          <PDFUpload isOpen={isPDFUploadOpen} onClose={() => setIsPDFUploadOpen(false)} />
        </div>




    






        // Desktop Layout
        : 
          <div className="flex h-screen w-full flex-col font-inter">
            {/* HEADER (unchanged, trimmed for brevity) */}
            <header
              role="banner"
              className="sticky top-0 z-50 w-full border-b border-gray-200"
            >
              <div className="w-full px-6">
                {/* ↑ increase row height and add vertical padding for breathing room */}
                <div className="h-[100px] grid grid-cols-[auto_1fr_auto] items-center gap-4 py-2">
                 
                
                  {/* Left: Toggle + Logo */}
                  <div className="flex items-center gap-3 min-w-0">
                    {/* Toggle (always visible, or keep md:hidden if you only want on small screens) */}
                    <button
                      type="button"
                      onClick={() => setModelBarOpen((v) => !v)}
                      className="inline-flex shrink-0 items-center justify-center rounded-lg p-3 bg-neutral-100 text-neutral-700 shadow-sm hover:bg-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 md:hidden flex"
                      aria-label="Toggle sidebar"
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M3 6h18M3 12h18M3 18h18"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>

                    <a href="#" className="inline-flex items-center gap-3 rounded-xl min-w-0">
                      <img
                        src="/NTU-LKCMedicine_Rev-Colour.png"
                        alt="LKCMedicine Logo"
                        className="shrink-0 w-72 h-auto object-contain"
                        draggable={false}
                        loading="lazy"
                      />
                    </a>
                  </div>


                  {/* Center spacer */}
                  <div className="hidden md:flex items-center justify-center min-w-0" />

                  {/* Right: Actions */}
                  <div className="flex items-center justify-end gap-2">
                    {/* Desktop buttons (slightly taller to match header height) */}
                    <div className="hidden md:flex items-center gap-2">
                      <button
                        onClick={() => setisInfoOpen(true)}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2.5 text-white text-sm font-medium shadow-sm transition hover:bg-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                        aria-haspopup="dialog"
                      >
                        <Info className="h-4 w-4" />
                        User Guide
                      </button>
                      <button
                        onClick={() => setIsNewsletterOpen(true)}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2.5 text-white text-sm font-medium shadow-sm transition hover:bg-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      >
                        <Mail className="h-4 w-4" />
                        Subscribe
                      </button>
                      <button
                        onClick={handleLogoutRedirect}
                        className="inline-flex items-center gap-2 rounded-lg bg-neutral-800 px-3 py-2.5 text-white text-sm font-semibold shadow-sm transition hover:bg-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600"
                      >
                        <LogOut className="h-4 w-4" />
                        Log Out
                      </button>
                    </div>

                    {/* Mobile: sidebar toggle + icons */}
                    <div className="flex md:hidden items-center gap-1.5">
                      <button
                        onClick={() => setisInfoOpen(true)}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2.5 text-white text-sm font-medium shadow-sm transition hover:bg-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                        aria-haspopup="dialog"
                      >
                        <Info className="h-4 w-4" />
                        User Guide
                      </button>
                      <button
                        onClick={() => setIsNewsletterOpen(true)}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2.5 text-white text-sm font-medium shadow-sm transition hover:bg-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      >
                        <Mail className="h-4 w-4" />
                        Subscribe
                      </button>
                      <button
                        onClick={handleLogoutRedirect}
                        className="inline-flex items-center gap-2 rounded-lg bg-neutral-800 px-3 py-2.5 text-white text-sm font-semibold shadow-sm transition hover:bg-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600"
                      >
                        <LogOut className="h-4 w-4" />
                        Log Out
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </header>


            {/* MAIN: TWO-PANE LAYOUT */}
            <div className="flex-1 min-h-0">
              <div className="w-full max-w-screen h-full md:mx-0 mx-auto">
                <div className="p-4 grid h-full grid-cols-1 md:grid-cols-[320px_1fr]">


                  

                  {/* LEFT SIDEBAR (model & settings) */}
                  <div
                    onClick={() => setModelBarOpen(false)}
                    aria-hidden="true"
                    className={[
                      "fixed inset-0 z-30 md:hidden",
                      "bg-black/50 backdrop-blur-sm transition-opacity duration-200",
                      modelBarOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
                    ].join(" ")}
                  />
                  <aside
                    data-sidebar
                    className={[
                      "sb-thin",
                      // positioning
                      "fixed z-40 md:z-auto md:static",
                      "top-[88px] left-0 h-[calc(100dvh-88px)] md:top-auto md:h-auto",
                      // width — responsive adjustment
                      "w-[88vw] max-w-[320px] md:w-full",
                      // surface
                      "bg-neutral-900 text-neutral-100 border border-neutral-700 rounded-none md:rounded-xl",
                      // scrolling
                      "overflow-y-auto overscroll-contain",
                      // padding + safe areas
                      // "p-4 md:p-4",
                      "pt-[calc(env(safe-area-inset-top)+16px)]",
                      "pb-[calc(env(safe-area-inset-bottom)+16px)]",
                      // drawer animation (mobile) vs static on md+
                      "transform transition-transform duration-300 ease-out md:transform-none",
                      modelBarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
                      // nice scrollbars (optional)
                      // "[scrollbar-gutter:stable] [scrollbar-width:thin] [scrollbar-color:theme(colors.violet.500)_transparent]"
                    ].join(" ")}
                  >
                    <div className="space-y-4">

                      <div className="space-y-4 mb-5">
                        {/* Title row */}
                        <div className="flex items-center justify-between border-b border-neutral-700 pb-3 px-4">
                          <h2 className="text-lg font-bold text-neutral-200">
                            Filter
                          </h2>
                        </div>

                        {/* SEARCH FILTER */}
                        <div className="relative px-4 pt-2">
                          <input
                            type="text"
                            data-tour="model-search"
                            value={modelSearch}
                            onChange={(e) => setModelSearch(e.target.value)}
                            placeholder="Search models…"
                            className="w-full rounded-lg border border-neutral-700 bg-neutral-900 text-neutral-100
                                      px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/60
                                      placeholder:text-neutral-500"
                            aria-label="Search models"
                          />
                          {modelSearch && (
                            <button
                              type="button"
                              onClick={() => setModelSearch("")}
                              className="absolute right-6 top-[calc(50%+4px)] -translate-y-1/2 p-1 rounded-md
                                        text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800
                                        transition-colors duration-150"
                              aria-label="Clear search"
                              title="Clear"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>



                      <h2 className="px-4 pt-2 text-md font-semibold text-neutral-400 tracking-wide">
                        Expertise
                      </h2>

                      {/* FILTER CHIPS */}
                      <div className="flex flex-wrap gap-2 px-4">
                        {FILTER_CHIPS.map((chip) => {
                          const active = selectedFilters.includes(chip); // ← new
                          return (
                            <button
                              key={chip}
                              type="button"
                              onClick={() => toggleChip(chip)}           // ← new
                              aria-pressed={active}
                              className={`rounded-full border px-3 py-1.5 text-xs transition
                                ${active
                                  ? "bg-violet-500 text-white border-violet-500"
                                  : "bg-neutral-800 text-neutral-200 border-violet-700 hover:bg-neutral-700"}
                                focus:outline-none focus:ring-2 focus:ring-violet-500/60`}
                            >
                              {chip}
                            </button>
                          );
                        })}
                      </div>

                      
                      

                      {/* Reasoning Effort */}
                      {(selectedModel === "o3-mini" ||
                        selectedModel === "azure-o3-mini" ||
                        selectedModel === "o1" ||
                        selectedModel === "o3" ||
                        selectedModel === "azure-o1") && (
                        <div className="space-y-1 px-4 pt-2">
                          <h2 className="pb-2 text-md font-semibold text-neutral-400 tracking-wide text-neutral-300">
                            Reasoning Effort
                          </h2>
                        
                          <select
                            id="reasoning-effort"
                            value={reasoningEffort}
                            onChange={(e) => setReasoningEffort(e.target.value)}
                            disabled={loading}
                            className="w-full rounded-lg border border-neutral-700 bg-neutral-900 text-neutral-100
                                      px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/60"
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                          </select>
                          <p className="text-xs text-neutral-400 pt-2 text-center">
                            <span className="font-medium text-neutral-300">
                              {String(reasoningEffort).toUpperCase()}:
                            </span>{" "}
                            {reasoningEffort === "low" && "Optimized for speed while maintaining solid reasoning."}
                            {reasoningEffort === "medium" && "Balanced approach for most tasks."}
                            {reasoningEffort === "high" && "Enhanced reasoning for complex problems."}
                          </p>
                        </div>
                      )}

                      {/* GPT-OSS Reasoning */}
                      {selectedModel && selectedModel.startsWith("gpt-oss-") && (
                        <div className="space-y-1 px-4 pt-2">
                          <label className="text-xs text-neutral-300">GPT-OSS Reasoning</label>
                          <select
                            value={gptOssReasoning}
                            onChange={(e) => setGptOssReasoning(e.target.value)}
                            disabled={loading}
                            className="w-full rounded-lg border border-neutral-700 bg-neutral-900 text-neutral-100
                                      px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/60"
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                          </select>
                        </div>
                      )}

                      {/* AI MODELS header */}
                      <div className="pt-4 px-4">
                        <h3 className="text-md font-semibold tracking-wide text-neutral-400">Models</h3>
                      </div>


                      {/* FILTERED MODEL LIST */}
                      <ul className="space-y-2 px-4">
                        {orderedModels.map(({ name, category }) => {
                          const active = selectedModel === name;

                          return (
                            <li
                              key={`${category}-${name}`}
                              className={active ? "sticky -top-4 z-10 bg-neutral-900/90 backdrop-blur" : ""}
                            >
                              <button
                                type="button"
                                onClick={() => handleModelChange(name)}
                                disabled={loading}
                                className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition
                                  ${active
                                    ? "border-purple-500 bg-purple-500/10 text-purple-300 shadow-sm"
                                    : "border-neutral-700 hover:bg-neutral-800 text-neutral-100"}
                                  focus:outline-none focus:ring-2 focus:ring-purple-500/60`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium">{name}</span>
                                  {active && (
                                    <span className="ml-2 rounded-full bg-purple-500 px-2 py-0.5 text-[10px] font-semibold text-white">
                                      Selected
                                    </span>
                                  )}
                                </div>
                              </button>
                            </li>
                          );
                        })}

                        {!nameMatched.length && (
                          <li className="px-1 text-xs text-neutral-400">No models match this filter.</li>
                        )}
                      </ul>


                      

                      
                    </div>
                  </aside>


                  {/* RIGHT: CHAT AREA */}
                  <main className="flex h-full min-h-0 flex-col">
                    {/* Info / warnings stack */}
                    <div className="px-4 md:px-6 pt-4 space-y-2">
                      {warningMessage && (
                        <div className="rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-900">
                          Warning: {warningMessage}
                        </div>
                      )}
                      {infoMessage && (
                        <div className="rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                          {infoMessage}
                        </div>
                      )}
                      {fileWarningMessage && (
                        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900">
                          {fileWarningMessage}
                        </div>
                      )}
                    </div>

                    {/* Chat History (scrollable) */}
                    <div
                      ref={scrollRef}
                      onScroll={handleScroll}
                      className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 py-4 sb-thin">
                      <InfoPopup isOpen={isInfoOpen} onClose={() => setisInfoOpen(false)} />

                      <div className="chat-history space-y-4 text-xl mx-auto">
                        {chatHistory.map((message, mIdx) => (
                          <div key={mIdx} 
                          className={`chat-message ${message.role}`}>
                            

                            {message.role === "system" ? (
                              <div className="w-full flex justify-center">
                                {typeof message.content === "string" ? (
                                  <div className="rounded-full bg-neutral-800/70 px-3 py-1 text-sm text-neutral-300">
                                    {message.content}
                                  </div>
                                ) : message.content?.type === "model-change" ? (
                                  <div className="rounded-full bg-neutral-800/70 px-3 py-1 text-sm text-neutral-300">
                                    Model changed to{" "}
                                    <span className="ml-1 inline-flex items-center rounded-full border border-purple-500/50 bg-purple-500/15 px-2 py-0.5 text-xs font-semibold text-purple-300">
                                      {message.content.model}
                                    </span>
                                  </div>
                                ) : (
                                  <div className="rounded-full bg-neutral-800/70 px-3 py-1 text-sm text-neutral-300">
                                    {JSON.stringify(message.content)}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="message-content">
                                {/* thinking bubble when assistant placeholder is "..." */}
                                {message.role === "assistant" && message.content === "..." ? (
                                  message.imageGenerating ? (
                                    <GeneratingImageLoader />      // ✨ for image-gen
                                  ) : (
                                    <TypingDots />                 // … for normal text
                                  )
                                  ) : Array.isArray(message.content) ? (
                                  (() => {
                                    const itemsWithIdx = message.content.map((it, i) => ({ ...it, __i: i }));
                                    const files  = itemsWithIdx.filter(it => it.type === "file");
                                    const images = itemsWithIdx.filter(it => it.type === "image_url");
                                    const texts  = itemsWithIdx.filter(it => it.type === "text");
                                    const isAssistant = message.role === "assistant";

                                    return (
                                      <div className="space-y-2">
                                        {/* FILE PILLS (top, no close button) */}
                                        {files.length > 0 && (
                                          <div className="flex flex-wrap gap-2">
                                            {files.map((it) => (
                                              <div
                                                key={`file-${it.__i}`}
                                                className="inline-flex items-center gap-3 rounded-3xl bg-[#262626] border border-[#4a4a4a] px-3 py-2"
                                              >
                                                <div className="h-10 w-10 rounded-2xl bg-[#ff4b4b] flex items-center justify-center">
                                                  {getFileIcon ? getFileIcon(it.name) : (
                                                    <span className="text-white text-xs font-bold">PDF</span>
                                                  )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                  <div className="text-sm font-medium text-neutral-50 truncate">
                                                    {it.name}
                                                  </div>
                                                  <div className="text-[11px] text-neutral-300">
                                                    {(it.name.split('.').pop() || 'FILE').toUpperCase()}
                                                  </div>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}

                                        {/* Images */}
                                        {images.map((it) => (
                                          <img
                                            key={`img-${it.__i}`}
                                            src={it.image_url}
                                            alt="Uploaded"
                                            className={
                                              "my-4 rounded cursor-zoom-in object-contain " +
                                              (isAssistant
                                                ? "w-full max-h-[480px]"
                                                : "w-auto max-h-40 md:max-h-56")
                                            }
                                            onClick={() => {
                                              const key = `${mIdx}:${it.__i}`;
                                              const gIdx = galleryIndexByKey.get(key);
                                              if (gIdx !== undefined) openChatLightboxAt(gIdx);
                                            }}
                                          />
                                        ))}

                                        {/* Text below files/images */}
                                        {texts.map((it) => (
                                          <p key={`txt-${it.__i}`}>{it.text}</p>
                                        ))}
                                      </div>
                                    );
                                  })()
                                ) : message.role === "assistant" ? (
                                  <AssistantMarkdown
                                    text={
                                      message.citations && message.citations.length
                                        ? formatResponseWithCitations(message.content, message.citations)
                                        : message.content
                                    }
                                    allowHtml={Boolean(message.citations && message.citations.length)}
                                  />
                                ) : message.citations && message.citations.length > 0 ? (
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: formatResponseWithCitations(message.content, message.citations)
                                    }}
                                  />
                                ) : (
                                  <p>{message.content}</p>
                                )}

                                {/* meta blocks kept outside the ternary, like before */}
                                {message.citations?.length > 0 && (
                                  <div className="citations">
                                    <details>
                                      <summary>Sources ({message.citations.length})</summary>
                                      <ul>
                                        {message.citations.map((citation, i) => (
                                          <li key={i}>
                                            <a href={citation} target="_blank" rel="noopener noreferrer">
                                              {new URL(citation).hostname}
                                            </a>
                                          </li>
                                        ))}
                                      </ul>
                                    </details>
                                  </div>
                                )}

                                {message.deepResearchData?.citations?.length > 0 && (
                                  <div className="deep-research-citations">
                                    <details>
                                      <summary>Deep Research Sources ({message.deepResearchData.citations.length})</summary>
                                      <ul>
                                        {message.deepResearchData.citations.map((c, i) => (
                                          <li key={i}>
                                            <a href={c.url} target="_blank" rel="noopener noreferrer">
                                              {c.title || new URL(c.url).hostname}
                                            </a>
                                          </li>
                                        ))}
                                      </ul>
                                    </details>
                                  </div>
                                )}

                                {message?.ragMetadata && (
                                  <div className="rag-metadata">
                                    <small>
                                      Referenced {message.ragMetadata.uploadedCount ?? 0}{" "}
                                      {(message.ragMetadata.uploadedCount ?? 0) === 1 ? "source" : "sources"} •
                                      Similarity: {(((message.ragMetadata.maxSimilarity ?? 0) * 100).toFixed(1))}%
                                    </small>
                                  </div>
                                )}
                              </div>

                            )}

                            {message.role === "assistant" && message.error && message.regeneratePrompt && (
                              <div className="my-auto mx-3 flex items-center justify-end">
                                <button
                                  onClick={() => handleRegenerate(message.regeneratePrompt)}
                                  disabled={loading}
                                  className={`
                                    group relative inline-flex items-center gap-2 
                                    rounded-full border-2 
                                    px-4 py-2
                                    text-sm font-medium
                                    transition-all duration-200 ease-out
                                    ${loading 
                                      ? "border-neutral-700 bg-neutral-900 text-neutral-500 cursor-not-allowed" 
                                      : "border-neutral-600 bg-neutral-900 text-neutral-200 hover:border-neutral-400 hover:bg-neutral-800 active:scale-95"
                                    }
                                  `}
                                >
                                  <svg
                                    className={`w-5 h-5 transition-transform duration-200 ${
                                      loading ? "opacity-50" : "group-hover:rotate-180"
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    strokeWidth={2}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                    />
                                  </svg>
                                  {/* <span>{loading ? "Regenerating..." : "Regenerate"}</span> */}
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    

                    {/* System Prompt (collapsible) */}
                      {selectedModel !== "o1-preview" &&
                      selectedModel !== "o1-mini" &&
                      selectedModel !== "azure-o1-mini" && (
                        
                        <div className="px-4 md:px-6 pb-2">
                          <div className="w-[80%] mx-auto rounded-2xl border border-neutral-700 bg-neutral-900 shadow
                                          px-3 py-1.5 md:py-2" data-tour="system-prompt">

                            {/* Top row: title + info + collapse + apply */}
                            <div
                              className="flex items-center justify-between gap-2 cursor-pointer"
                              onClick={togglePromptVisibility}   // whole header toggles
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-[12px] font-semibold tracking-wide text-neutral-200 uppercase">
                                  System Prompt
                                </span>

                                {/* info tooltip */}
                                <div className="relative group">
                                  <button
                                    data-tooltip-id="help-tip"
                                    data-tooltip-place="top"
                                    data-tooltip-delay-show={200}
                                    aria-label="What does this do?"
                                    className="h-5 w-5 grid place-items-center text-neutral-300 hover:text-white"
                                    onClick={(e) => {
                                      e.stopPropagation(); // don't toggle
                                    }}
                                  >
                                    <Info size={18} />
                                  </button>

                                  <Tooltip
                                    id="help-tip"
                                    clickable={false}
                                    place="top"
                                    effect="solid"
                                    delayShow={200}
                                    style={{
                                      backgroundColor: "#2d3748",
                                      color: "white",
                                      padding: "6px 12px",
                                      borderRadius: "10px",
                                      fontSize: "0.875rem",
                                      boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                                      maxWidth: "220px",
                                      textAlign: "center",
                                      zIndex: 9999,
                                    }}
                                  >
                                    Set rules for how the assistant should behave for this chat.
                                  </Tooltip>
                                </div>

                                {dirty && (
                                  <span className="inline-flex items-center gap-1.5 rounded-full border border-red-500/40 bg-red-500/10 px-2 py-1 text-[11px] text-red-300">
                                    <TriangleAlert className="h-3.5 w-3.5 text-red-400" aria-hidden="true" />
                                    <span>Unsaved</span>
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center gap-2">
                                <button
                                  className="p-1 rounded hover:bg-neutral-800 text-neutral-300 hover:text-white"
                                  onClick={(e) => {
                                    e.stopPropagation(); // prevent double toggle
                                    togglePromptVisibility();
                                  }}
                                  aria-label="Collapse/Expand"
                                >
                                  {isPromptVisible ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                                </button>

                                
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleApplySystemPrompt();
                                  }}
                                  className={`ml-1 h-10 w-10 grid place-items-center rounded-full transition
                                    ${(applied && !dirty)
                                      ? "bg-emerald-500 text-white border hover:border-white border-emerald-500 shadow-md"
                              
                                      : "bg-neutral-800 text-neutral-200 hover:bg-neutral-700"}`}
                                  aria-label={(applied && !dirty) ? "Saved" : (dirty ? "Save changes" : "Save")}
                                  title={(applied && !dirty) ? "Saved" : (dirty ? "Save changes" : "Save")}
                                >
                                  {(applied && !dirty) ? <Check size={16} /> : <Save size={16} />}
                                </button>
                              </div>
                            </div>

                            {/* Body */}
                            <div
                              className={[
                                "mt-1 rounded-xl border border-neutral-800 bg-neutral-900/60",
                                "transition-[max-height,opacity] duration-200",
                                isPromptVisible
                                  ? "p-2 opacity-100 max-h-[400px]"          // show
                                  : "p-0 opacity-0 max-h-0 overflow-hidden pointer-events-none" // hide
                              ].join(" ")}
                              aria-hidden={!isPromptVisible}
                            >
                                
                                <div
                                  ref={editorRef}
                                  role="textbox"
                                  aria-multiline="true"
                                  contentEditable
                                  suppressContentEditableWarning
                                  onInput={handleEditableInput}
                                  onBlur={() => {
                                    // remove phantom <br> so :empty placeholder works
                                    const el = editorRef.current;
                                    if (!el) return;
                                    if (!el.textContent?.trim()) el.textContent = "";
                                  }}
                                  className="min-h-[40px] max-h-32 overflow-auto outline-none
                                            text-[13px] leading-5 text-neutral-100 whitespace-pre-wrap"
                                  data-placeholder={`Set a constraint eg: "You are a helpful assistant that explains complex topics in simple terms."`}
                                />

                                {/* helper chips */}
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {[
                                    "Act as…",
                                    "Context:",
                                    "Tone:",
                                    "Format:",
                                    // "Constraints:",
                                    // "Explain step-by-step",
                                    // "Examples:"
                                  ].map((t) => (
                                    <button
                                      key={t}
                                      type="button"
                                      onClick={() => insertChip(t)}
                                      className="rounded-full border border-neutral-700 bg-neutral-800
                                                px-2 py-1 text-[12px] text-neutral-200 hover:bg-neutral-700"
                                    >
                                      {t}
                                    </button>
                                  ))}
                                </div>

                                {/* small hint */}
                                {dirty && (
                                  <p className="mt-2 text-[11px] text-neutral-400">
                                    You have unsaved changes. Click <span className="text-emerald-400">Save</span> (arrow) to apply.
                                  </p>
                                )}
                              </div>
                            
                          </div>
                        </div>

                      )}


                    {/* Composer */}
                    <div className="px-4 pb-6 md:px-6">
                      {/* single bordered wrapper like ChatGPT */}
                      <div className="border rounded-3xl bg-[#3b3b3b] px-4 pb-2 w-[80%] mx-auto">
                        {/* Previews sit INSIDE the same border, above the textarea */}
                        {imagePreviews.length > 0 && (
                          <div className="flex gap-3 overflow-x-auto py-2">
                            {imagePreviews.map((preview, i) => (
                              <div
                                key={i}
                                className="relative h-24 w-24 shrink-0 rounded-2xl overflow-hidden border border-neutral-700 bg-neutral-800"
                              >
                                
                              <img
                                key={preview.name ?? i}
                                src={preview.url}
                                alt=""
                                className="h-24 w-24 object-cover rounded-2xl cursor-zoom-in"
                                onClick={() => openComposerLightboxAt(i)}
                              />


                                <button
                                  onClick={() => removeFile(preview.name)}
                                  className="absolute right-1 top-1 h-6 w-6 rounded-full bg-black/60 text-white text-xs hover:bg-red-600"
                                  aria-label="Remove Preview"
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* FILE TAB(S) LIKE CHATGPT */}
                        {nonImageFiles.length > 0 && (
                          <div className="mt-2">
                            <div className="flex flex-wrap gap-2">
                              {nonImageFiles.map((file) => {
                                const uploadedFile = uploadedFiles.find(u => u.filename === file.name);
                                const embeddingStatus = uploadedFile?.embeddingStatus;
                                const directStatus = uploadedFile?.status;
                                const isProcessing = processingFiles.includes(file.name);

                                const hasCompleteStatus =
                                  embeddingStatus === "completed" ||
                                  embeddingStatus === "failed" ||
                                  directStatus === "uploaded" ||
                                  directStatus === "failed";

                                const ext = (file.name.split(".").pop() || "").toUpperCase() || "FILE";
                                const showSpinner = isProcessing && !hasCompleteStatus;

                                return (
                                  <div
                                    key={file.name}
                                    className="flex items-center gap-2 rounded-2xl bg-[#2b2b2b] border border-[#555] px-3 py-1.5"
                                  >
                                    {/* red square */}
                                    <div className="h-7 w-7 rounded-xl bg-[#2b2b2b] flex items-center justify-center">
                                      {showSpinner ? (
                                        <div className="h-4 w-4 border-2 border-white/35 border-t-white rounded-full animate-spin" />
                                      ) : (
                                        getFileIcon(file.name)
                                      )}
                                    </div>

                                    {/* filename + type */}
                                    <div className="flex flex-col text-xs leading-tight">
                                      <span className="font-semibold text-white break-all">
                                        {file.name}
                                      </span>
                                      <span className="text-[9px] text-gray-300">{ext}</span>
                                    </div>

                                    {/* close */}
                                    <button
                                      onClick={() => handleRemoveComposerFile(file.name)}
                                      disabled={isProcessingUpload}
                                      className={`ml-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/80
                                                  text-white text-[9px] hover:bg-black ${
                                                    isProcessingUpload ? "cursor-not-allowed" : ""
                                                  }`}
                                      aria-label={`Remove ${file.name}`}
                                      title={`Remove ${file.name}`}
                                    >
                                      <X size={10} />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}





                        {/* Input row (unchanged styles) */}
                        <div className="input-container flex flex-col w-full">
                          <textarea
                            ref={taRef}
                            rows={textAreaRows}
                            placeholder="Ask anything..."
                            value={userPrompt}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            className="flex-1 resize-y rounded-lg border border-gray-300 px-3 pt-6 text-sm focus:outline-none"
                          />

                          {/* Controls row */}
                          <div className="controls flex flex-wrap items-center gap-2 pt-3">

                            {/* toggle on the far left */}
                            <button
                              onClick={handleClearConversation}
                              aria-label="Clear Conversation"
                              title="Clear Conversation"
                              disabled={loading}
                              data-tour="clear"
                              className={`clear-button icon-btn bg-[#f44336] border-2 text-neutral-200 transition-all duration-200 ease-out transform
                                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60
                                ${!loading ? "hover:scale-105 active:scale-95" : ""}
                                border-[#e53935] text-neutral-300 hover:border-neutral-300
                                
                                `}
                            >
                              {/* brush icon */}
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-brush-cleaning-icon lucide-brush-cleaning">
                                <path d="m16 22-1-4"/>
                                <path d="M19 13.99a1 1 0 0 0 1-1V12a2 2 0 0 0-2-2h-3a1 1 0 0 1-1-1V4a2 2 0 0 0-4 0v5a1 1 0 0 1-1 1H6a2 2 0 0 0-2 2v.99a1 1 0 0 0 1 1"/>
                                <path d="M5 14h14l1.973 6.767A1 1 0 0 1 20 22H4a1 1 0 0 1-.973-1.233z"/>
                                <path d="m8 22 1-4"/>
                              </svg>
                            </button>

                            {/* MORE menu */}
                            <div
                              ref={moreMenuRef}
                              title="More tools"
                              className="relative flex items-center gap-2"
                            >
                              <button
                                type="button"
                                onClick={() => setShowMoreOptions(prev => !prev)}
                                aria-label="More tools"
                                data-tour="more-tools"
                                className={`icon-btn h-12 w-12 flex items-center justify-center rounded-full border-2 bg-neutral-900 text-neutral-200 transition-all duration-200 ease-out transform
                                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60
                                ${!loading ? "hover:scale-105 active:scale-95" : ""}
                                ${
                                  showMoreOptions
                                    ? "border-violet-400 text-violet-300 bg-violet-950/50"
                                    : "border-neutral-600 hover:border-white hover:text-white"
                                }`}
                              >
                                <Grid2x2Plus size={25} />
                              </button>

                              <div
                                className={`
                                  absolute z-20 bottom-full left-1/2 mb-3 min-w-[240px] ml-4
                                  rounded-xl border-2 border-neutral-700/80 bg-neutral-900/95 backdrop-blur-sm shadow-2xl
                                  p-2 flex flex-col gap-1.5
                                  transform -translate-x-1/2 origin-bottom
                                  transition-all duration-200 ease-out
                                  ${
                                    showMoreOptions
                                      ? "opacity-100 scale-100 translate-y-0 pointer-events-auto"
                                      : "opacity-0 scale-95 translate-y-2 pointer-events-none"
                                  }
                                `}
                              >
                                {[
                                  {
                                    type: "react", // use Lucide icon
                                    icon: Plus,
                                    text: "Upload document",
                                    action: toggleFilePopup,
                                  },
                                  {
                                    type: "svg",    // use SVG from /public
                                    src: "/speech_to_text.svg",
                                    text: "Audio transcription",
                                    action: () => setIsAudioPopupOpen(true),
                                  },
                                  {
                                    type: "svg",    // use SVG from /public
                                    src: "/PDF_to_PPT.svg",
                                    text: "Summarise PDF to PPTX",
                                    action: () => setIsPDFUploadOpen(true),
                                  },
                                ].map((item, idx) => (
                                  <button
                                    key={idx}
                                    onClick={item.action}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-neutral-200 hover:bg-neutral-800/80 hover:text-violet-200 transition-all duration-150 group"
                                    style={{
                                      animation: showMoreOptions
                                        ? `slideIn 0.25s ease-out ${idx * 0.05}s both`
                                        : "none",
                                    }}
                                  >
                                    {item.type === "react" ? (
                                      <item.icon
                                        size={20}
                                        className="group-hover:scale-110 transition-transform duration-150"
                                      />
                                    ) : (
                                      <img
                                        src={item.src}
                                        alt=""
                                        className="h-5 w-5 invert group-hover:scale-110 transition-transform duration-150"
                                      />
                                    )}

                                    <span className="font-medium">{item.text}</span>
                                  </button>
                                ))}
                              </div>
                            </div>

                          <div className="mr-auto flex items-center gap-3 mr-4">
                            {/* Icon toggle button */}
                            <button
                              type="button"
                              title={enableImageGen ? "Disable image generation" : "Enable image generation"}
                              onClick={() => setEnableImageGen(prev => !prev)}
                              aria-pressed={enableImageGen}
                              aria-label={enableImageGen ? "Disable image generation" : "Enable image generation"}
                              data-tour="image-generation-toggle"
                              className={`
                                relative overflow-hidden
                                flex items-center justify-center
                                h-12 w-12 rounded-full border-2 text-base
                                transition-all duration-200 ease-out transform
                                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60
                                ${!loading ? "hover:scale-105 active:scale-95" : ""}
                                ${
                                  enableImageGen
                                    ? "border-violet-300 text-white shadow-lg shadow-violet-500/40"
                                    : "bg-neutral-900 border-neutral-600 text-neutral-300 hover:border-neutral-300"
                                }
                              `}
                            >
                              {/* Sliding background effect */}
                              <span
                                className={`
                                  absolute inset-0 bg-violet-500 rounded-full
                                  transition-transform duration-300 ease-out
                                  ${enableImageGen ? "translate-x-0 translate-y-0" : "translate-x-[-150%] translate-y-[150%]"}
                                `}
                                style={{
                                  transformOrigin: 'bottom right'
                                }}
                              />
                              
                              {/* Icon - positioned above the sliding background */}
                              <span className="relative z-10">
                                {enableImageGen ? (
                                  <Image size={25} />
                                ) : (
                                  <ImageOff size={25} />
                                )}
                              </span>
                            </button>
                          </div>


                            
                            {/* left utilities */}
                            <div className="flex items-center gap-2">
                            
                              {enableImageGen && imagePreviews.length === 1 && (
                                <button onClick={() => handleEditImage(imagePreviews[0])} 
                                className={`inpainting-button icon-btn border-2
                                  text-neutral-200 transition-all duration-200 ease-out transform
                                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60
                                  ${!loading ? "hover:scale-105 active:scale-95" : ""}
                                  ${loading ? "opacity-60 cursor-not-allowed" : ""}
                                  bg-neutral-900 border-neutral-300 text-white hover:border-neutral-300
                                `}

                                aria-label="Inpainting">
                                  Mask
                                </button>
                              )}
                            </div>

                            {/* COLOURED BUTTONS — stay together */}
                            <div className="btn-cluster flex items-center gap-2 shrink-0 min-w-max">
                              
                              <button onClick={() => handleSendPrompt()} aria-label="Send" title="Send Prompt" disabled={loading} data-tour="send" className="submit-button icon-btn bg-[#4caf50]">
                                {loading ? <Loader size={20} className="loading-icon" /> : <Send size={20} />}
                              </button>
                              
                            </div>

                            
                          </div>

                        </div>

                      </div>
                    </div>

                  </main>
                </div>
              </div>
            </div>

            {lightboxOpen && currentArray.length > 0 && (
              <div
                className="fixed inset-0 z-[10000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                onClick={closeLightbox}
                role="dialog"
                aria-modal="true"
              >
                {/* Close (top-right) */}
                <button
                  onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
                  className="fixed right-3 top-3 h-10 w-10 rounded-full bg-black/70 text-white grid place-items-center"
                  aria-label="Close"
                  title="Close"
                >
                  <X className="h-5 w-5" />
                </button>

                {/* Image wrapper (clicks here won't close) */}
                <div
                  className="relative max-w-[95vw] max-h-[95vh]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <img
                    src={(lightboxSource === "composer" ? imagePreviews : gallery)[lightboxIndex].url}
                    alt=""
                    className="max-w-[95vw] max-h-[95vh] object-contain rounded-lg shadow-2xl"
                    draggable={false}
                  />
                </div>

                {/* Fixed-position nav controls (independent of image size) */}
                {total > 1 && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); goPrev(); }}
                      className="fixed left-12 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-black/70 text-white grid place-items-center"
                      aria-label="Previous"
                      title="Previous"
                    >
                      <ChevronLeft className="h-7 w-7" />
                    </button>

                    <button
                      onClick={(e) => { e.stopPropagation(); goNext(); }}
                      className="fixed right-12 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-black/70 text-white grid place-items-center"
                      aria-label="Next"
                      title="Next"
                    >
                      <ChevronRight className="h-7 w-7" />
                    </button>

                    <span className="fixed bottom-3 left-1/2 -translate-x-1/2 text-xs text-white/80">
                      {lightboxIndex + 1} / {total}
                    </span>
                  </>
                )}
              </div>
            )}





            {/* Popups kept outside layout so they overlay properly */}
            <InfoPopup isOpen={isInfoOpen} onClose={() => setisInfoOpen(false)} />
            {isNewsletterOpen && (
              <NewsletterPopup isOpen={isNewsletterOpen} onClose={() => setIsNewsletterOpen(false)} />
            )}
            {!isNewsletterOpen && isUpdatesOpen && (
              <UpdatesPopup isOpen={isUpdatesOpen} onClose={() => setIsUpdatesOpen(false)} />
            )}
            {isEditPopupOpen && (
              <ImageEditor
                imageToEdit={imageToEdit}
                setIsEditPopupOpen={setIsEditPopupOpen}
                setMaskPreview={setMaskPreview}
                maskPreview={maskPreview}
                maskSavedMessage={maskSavedMessage}
                setMaskSavedMessage={setMaskSavedMessage}
              />
            )}
            <FileUpload
              isOpen={isFilePopupOpen}
              onClose={toggleFilePopup}
              uploadMode={uploadMode}
              setUploadMode={handleModeSwitch}
              fileList={fileList}
              setFileList={setFileList}
              fileWarningMessage={fileWarningMessage}
              setFileWarningMessage={setFileWarningMessage}
              setIsS3UploadEnabled={setIsS3UploadEnabled}
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFiles}
              extractedText={extractedText}
              setExtractedText={setExtractedText}
              imagePreviews={imagePreviews}
              setImagePreviews={setImagePreviews}
              isDirectUploadModelSelected={
                selectedModel === "gpt-4o" || selectedModel === "gpt-4o-mini" ||
                selectedModel === "gpt-5" || selectedModel === "gpt-5-mini" || selectedModel === "gpt-5-nano"
              }
            />
            {isPopupOpen && (
              <div className="popup">
                <div className="popup-content">
                  <h2>Edit System Prompt</h2>
                  <textarea
                    value={systemPrompt}
                    onChange={(e) => setSystemPrompt(e.target.value)}
                    rows="12"
                    cols="25"
                  />
                  <button onClick={togglePopup}>Confirm</button>
                </div>
              </div>
            )}
            <AudioTranscription isOpen={isAudioPopupOpen} onClose={() => setIsAudioPopupOpen(false)} />
            <PDFUpload isOpen={isPDFUploadOpen} onClose={() => setIsPDFUploadOpen(false)} />
          </div>
      }
    </>
    
    
  );
}

export default GenericAI;
